import {
  GOAL_DEPTH,
  MARGIN,
  POST_R,
  GameState,
  GameEvent,
  MapConfig,
  createGame,
  createInputState,
  updateGame,
  mapById,
  LoopbackSession,
  PredictiveClient,
  InputState,
  MatchRules,
  RoomPhase,
  ControlAction,
  PeerInfo,
  ReportReason,
  DEFAULT_TEAM_NAMES,
  KICKOFF_RADIUS,
  RemoteKick,
  maxKickSpeed,
} from "@tak/sim";
import { attachInput } from "./input";
import { attachTouchControls, shouldShowTouch, detectPointerEnv } from "./controls/touch";
import { readViewport, onViewportChange, canvasSize, effectiveZoom, zoomForLevel, ZOOM_VIEWS } from "./viewport";
import { teamStyles, TEAM_COLORS, TEAM_RGB, BALL_COLOR, BALL_RGB, applyUiScale, applyVision, type TeamStyle } from "./a11y";
import { WsNetClient, ControlOpts, ConnState, ChatLine } from "./net/wsTransport";
import { WtNetClient } from "./net/wtTransport";
import { AudioEngine } from "./audio";
import {
  ParticleSystem, TrailSystem, RingSystem, SUPER_KICK_INTENSITY,
  ballTrailStrength, BALL_TRAIL_SAMPLES,
} from "./effects";
import { UIManager, loadSettings, Settings, Quality, EXTRAPOLATION_BIAS } from "./ui";
import { MenuBackdrop } from "./menuBackdrop";
import { drawGoalAnim } from "./render/goalAnim";
import { drawPlayer } from "./render/disc";
import { LobbyUI } from "./lobby/lobbyUI";
import { OfflineRoomListProvider } from "./lobby/offlineProvider";
import { MultiRegionRoomListProvider } from "./lobby/httpProvider";
import { RegionId } from "./lobby/contract";
import { regionWsUrl, regionHttpBase, configuredRegions, hasBackend, warmUpBackend } from "./lobby/regions";
import { NameScreen } from "./lobby/nameScreen";
import { PhaseOverlay } from "./lobby/phaseOverlay";
import { ConnBanner } from "./lobby/connBanner";
import { LegalScreen } from "./lobby/legalScreen";
import { loadPlayerName, savePlayerName, getGuestId, getSessionId } from "./lobby/identity";
import { ticketNow } from "./lobby/account";
import * as account from "./lobby/account";
import { inviteUrl, readInvite } from "./lobby/invite";
import { iconGlyph } from "./lobby/profile";
import { installClientErrorReporting, breadcrumb } from "./obs/clientLog";

const FIXED_DT = 1 / 60;
const MAX_STEPS_PER_FRAME = 5;

/** Nº de niveles de cámara (LEJOS / MEDIO / CERCA). El zoom de cada uno ya no
 *  es un número fijo: sale de cuánta cancha debe verse en esa pantalla (ver
 *  `zoomForLevel`), que es lo que hace que "CERCA" sea cerca en todas. */
const ZOOM_LEVELS = ZOOM_VIEWS;
const CAM_LERP = 0.12;
const CAM_ZOOM_LERP = 0.12;
const CAM_LOOKAHEAD = 0.22;
/** Cuánto se desplaza el encuadre hacia el balón (0 = solo tú, 1 = solo balón). */
const CAM_BALL_WEIGHT = 0.38;
/** Aire (px de mundo) alrededor de disco y balón al calcular el zoom de encuadre. */
const CAM_FRAME_PAD = 260;
/** Hasta dónde puede abrirse el zoom respecto al nivel elegido. Con el balón en
 *  la otra punta, alejarse hasta verlo dejaría los discos en un píxel. */
const CAM_MIN_ZOOM_FACTOR = 0.62;

function setupCanvas(): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.getElementById("game") as HTMLCanvasElement | null;
  if (!canvas) throw new Error("canvas#game no encontrado");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context no disponible");
  return [canvas, ctx];
}

// Paleta del campo. Los dos acentos NO se escriben aquí: salen de `TEAM_COLORS`
// (src/a11y.ts), que es también el duotono del sistema de diseño. Tener el
// color del equipo escrito en dos sitios es tenerlo mal en uno de los dos.
const FIELD_DARK = "#080d13";
const FIELD_LIGHT = "#0d151d";
const LINE_TEAL = "rgba(34,230,255,0.32)";
const FRAME_BG = "#05070b";
const ACCENT_RED = TEAM_COLORS[0];
const ACCENT_TEAL = TEAM_COLORS[1];
// Curva mínima: los bordes se ven rectos y fieles a la hitbox cuadrada.
const CORNER_R = 6;

/* Las mismas familias que el sistema de diseño (`src/design/system.ts`). El
   canvas no puede leer variables CSS, así que se repiten aquí — con el mismo
   orden, para que el HUD y los menús se vean escritos por la misma mano. */
const DISPLAY_STACK = '"Archivo Black","Franklin Gothic Bold","Arial Narrow",Impact,"Segoe UI",system-ui,sans-serif';
const UI_STACK = '"Segoe UI Semibold","Segoe UI",system-ui,sans-serif';
const MONO_STACK = '"JetBrains Mono","SF Mono",ui-monospace,Consolas,monospace';

function renderPitchToCanvas(map: MapConfig): HTMLCanvasElement {
  const CW = map.courtW;
  const CH = map.courtH;
  const GH = map.goalH;
  const off = document.createElement("canvas");
  off.width = CW + MARGIN * 2;
  off.height = CH + MARGIN * 2;
  const c = off.getContext("2d")!;
  c.translate(MARGIN, MARGIN);

  const totalW = CW + MARGIN * 2;
  const totalH = CH + MARGIN * 2;

  // Fondo del margen (fuera del campo)
  c.fillStyle = FRAME_BG;
  c.fillRect(-MARGIN, -MARGIN, totalW, totalH);

  const gy0 = (CH - GH) / 2;
  const gy1 = (CH + GH) / 2;

  // Superficie del campo con esquinas redondeadas y bandas sutiles
  const CR = Math.min(CORNER_R, GH / 2 - 4);
  c.save();
  roundedFieldPath(c, 0, 0, CW, CH, CR);
  c.clip();
  const grad = c.createLinearGradient(0, 0, 0, CH);
  grad.addColorStop(0, FIELD_LIGHT);
  grad.addColorStop(1, FIELD_DARK);
  c.fillStyle = grad;
  c.fillRect(0, 0, CW, CH);
  const stripes = 12;
  const sw = CW / stripes;
  for (let s = 0; s < stripes; s += 2) {
    c.fillStyle = "rgba(255,255,255,0.015)";
    c.fillRect(s * sw, 0, sw, CH);
  }
  c.restore();

  // Borde exterior redondeado con hueco en las porterías
  c.strokeStyle = LINE_TEAL;
  c.lineWidth = 3;
  c.beginPath();
  c.moveTo(CR, 0);
  c.lineTo(CW - CR, 0);
  c.arcTo(CW, 0, CW, CR, CR);
  c.lineTo(CW, gy0);
  c.moveTo(CW, gy1);
  c.lineTo(CW, CH - CR);
  c.arcTo(CW, CH, CW - CR, CH, CR);
  c.lineTo(CR, CH);
  c.arcTo(0, CH, 0, CH - CR, CR);
  c.lineTo(0, gy1);
  c.moveTo(0, gy0);
  c.lineTo(0, CR);
  c.arcTo(0, 0, CR, 0, CR);
  c.stroke();

  // Línea y círculo central
  c.beginPath();
  c.moveTo(CW / 2, 0);
  c.lineTo(CW / 2, CH);
  c.stroke();
  c.beginPath();
  // El círculo central ES el círculo de saque: mismo radio que aplica la regla
  // (ver `KICKOFF_RADIUS` en @tak/sim). Un círculo pintado que no significa nada
  // es peor que no pintarlo.
  c.arc(CW / 2, CH / 2, KICKOFF_RADIUS, 0, Math.PI * 2);
  c.stroke();
  c.fillStyle = LINE_TEAL;
  c.beginPath();
  c.arc(CW / 2, CH / 2, 5, 0, Math.PI * 2);
  c.fill();

  // Áreas con esquinas redondeadas
  const boxH = Math.min(240, GH + 90);
  const boxDepth = 110;
  const boxTop = (CH - boxH) / 2;
  const boxR = 8;
  c.strokeStyle = LINE_TEAL;
  c.lineWidth = 3;
  c.beginPath();
  c.moveTo(0, boxTop);
  c.lineTo(boxDepth - boxR, boxTop);
  c.arcTo(boxDepth, boxTop, boxDepth, boxTop + boxR, boxR);
  c.lineTo(boxDepth, boxTop + boxH - boxR);
  c.arcTo(boxDepth, boxTop + boxH, boxDepth - boxR, boxTop + boxH, boxR);
  c.lineTo(0, boxTop + boxH);
  c.moveTo(CW, boxTop);
  c.lineTo(CW - boxDepth + boxR, boxTop);
  c.arcTo(CW - boxDepth, boxTop, CW - boxDepth, boxTop + boxR, boxR);
  c.lineTo(CW - boxDepth, boxTop + boxH - boxR);
  c.arcTo(CW - boxDepth, boxTop + boxH, CW - boxDepth + boxR, boxTop + boxH, boxR);
  c.lineTo(CW, boxTop + boxH);
  c.stroke();

  // Redes de portería (rojo izquierda, turquesa derecha)
  c.lineWidth = 2;
  c.strokeStyle = "rgba(255,46,99,0.4)";
  c.beginPath();
  c.moveTo(0, gy0);
  c.lineTo(-GOAL_DEPTH, gy0);
  c.lineTo(-GOAL_DEPTH, gy1);
  c.lineTo(0, gy1);
  c.stroke();
  c.strokeStyle = "rgba(34,230,255,0.4)";
  c.beginPath();
  c.moveTo(CW, gy0);
  c.lineTo(CW + GOAL_DEPTH, gy0);
  c.lineTo(CW + GOAL_DEPTH, gy1);
  c.lineTo(CW, gy1);
  c.stroke();

  // LÍNEA DE GOL. Es la única línea del campo que decide algo —el balón tiene
  // que cruzarla ENTERO— y era justo la que no estaba dibujada: el borde del
  // campo se interrumpía en la boca de la portería y quedaba un hueco. Sin ella
  // no hay forma de juzgar un gol ajustado: se ve el disco entre los postes y no
  // se sabe si ha entrado. Va del color de la portería y por debajo de los
  // postes, que la rematan por los extremos.
  c.save();
  c.lineWidth = 3;
  c.lineCap = "butt";
  for (const [x, color] of [[0, ACCENT_RED], [CW, ACCENT_TEAL]] as [number, string][]) {
    c.shadowColor = color;
    c.shadowBlur = 6;
    c.strokeStyle = color;
    c.globalAlpha = 0.75;
    c.beginPath();
    c.moveTo(x, gy0);
    c.lineTo(x, gy1);
    c.stroke();
  }
  c.restore();

  bakePost(c, 0, gy0, ACCENT_RED);
  bakePost(c, 0, gy1, ACCENT_RED);
  bakePost(c, CW, gy0, ACCENT_TEAL);
  bakePost(c, CW, gy1, ACCENT_TEAL);

  return off;
}

function roundedFieldPath(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

function bakePost(c: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  c.save();
  c.shadowColor = color;
  c.shadowBlur = 7;
  c.beginPath();
  c.arc(x, y, POST_R, 0, Math.PI * 2);
  c.fillStyle = color;
  c.fill();
  c.restore();
  c.beginPath();
  c.arc(x, y, POST_R * 0.55, 0, Math.PI * 2);
  c.fillStyle = "rgba(255,255,255,0.85)";
  c.fill();
}

let pitchCanvas: HTMLCanvasElement | null = null;
let pitchMapId = "";
function getPitchCanvas(map: MapConfig): HTMLCanvasElement {
  if (!pitchCanvas || pitchMapId !== map.id) {
    pitchCanvas = renderPitchToCanvas(map);
    pitchMapId = map.id;
  }
  return pitchCanvas;
}

interface Camera {
  x: number;
  y: number;
  zoom: number;
  zoomIndex: number;
}

function clampCamera(cam: Camera, vw: number, vh: number, cw: number, ch: number): void {
  const halfW = vw / (2 * cam.zoom);
  const halfH = vh / (2 * cam.zoom);
  const worldL = -MARGIN + halfW;
  const worldR = cw + MARGIN - halfW;
  const worldT = -MARGIN + halfH;
  const worldB = ch + MARGIN - halfH;
  cam.x = worldR <= worldL ? cw / 2 : Math.max(worldL, Math.min(worldR, cam.x));
  cam.y = worldB <= worldT ? ch / 2 : Math.max(worldT, Math.min(worldB, cam.y));
}



/** Etiqueta de nombre sobre un disco, en espacio de mundo (tamaño ~constante en
 *  pantalla dividiendo por el zoom de cámara). */
function drawNameTag(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, zoom: number, color: string): void {
  ctx.save();
  const fs = 13 / zoom;
  ctx.font = `700 ${fs}px "Segoe UI",system-ui,sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.shadowColor = "rgba(0,0,0,0.85)";
  ctx.shadowBlur = 5 / zoom;
  ctx.fillStyle = color;
  ctx.fillText(text, x, y - 6 / zoom);
  ctx.restore();
}

/**
 * Flechas en el BORDE de la pantalla para lo que se ha salido de cuadro.
 *
 * La cámara encuadra tu disco y el balón, así que un rival que se va a su
 * portería —o un compañero al otro lado— desaparece. Y "no veo al otro jugador"
 * es, con razón, lo que más se ha repetido: no basta con que la cámara sea
 * razonable, hay que SABER dónde está todo el mundo. Cada flecha va del color de
 * su equipo, apunta desde el centro y lleva la distancia en metros de cancha.
 *
 * La alternativa (abrir el zoom hasta que todo quepa) deja los discos en cuatro
 * píxeles: se ve todo y no se juega nada.
 */
function drawEdgeMarkers(
  ctx: CanvasRenderingContext2D, g: GameState, cam: Camera,
  vw: number, vh: number, dpr: number, roster: RenderRoster,
): void {
  const w = g.world;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const cx = vw / 2;
  const cy = vh / 2;
  const margin = 34;
  const halfW = Math.max(10, vw / 2 - margin);
  const halfH = Math.max(10, vh / 2 - margin);

  /** Pinta una flecha hacia un punto de mundo si está fuera de cuadro. */
  const mark = (wx: number, wy: number, color: string, label: string): void => {
    // Mundo → pantalla.
    const sx = (wx - cam.x) * cam.zoom;
    const sy = (wy - cam.y) * cam.zoom;
    if (Math.abs(sx) <= halfW && Math.abs(sy) <= halfH) return; // se ve: no hace falta
    // Punto del borde en esa dirección (proyección sobre el rectángulo).
    const k = Math.min(halfW / Math.max(1e-3, Math.abs(sx)), halfH / Math.max(1e-3, Math.abs(sy)));
    const ex = cx + sx * k;
    const ey = cy + sy * k;
    const ang = Math.atan2(sy, sx);
    ctx.save();
    ctx.translate(ex, ey);
    ctx.rotate(ang);
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(11, 0);
    ctx.lineTo(-8, 7);
    ctx.lineTo(-8, -7);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    if (label) {
      ctx.save();
      ctx.globalAlpha = 0.85;
      ctx.fillStyle = color;
      ctx.font = `700 11px ${MONO_STACK}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      // La etiqueta se mete HACIA DENTRO de la flecha, o se saldría de pantalla.
      ctx.fillText(label, ex - Math.cos(ang) * 22, ey - Math.sin(ang) * 22);
      ctx.restore();
    }
  };

  for (let i = 0; i < g.players.length; i++) {
    const body = g.players[i];
    if (body < 0 || g.active[i] === false || i === roster.localSlot || roster.ghostOf(i)) continue;
    const style = teamStyle[roster.teamOf(i)] ?? teamStyle[0];
    mark(w.px[body], w.py[body], style.color, roster.nameOf(i).slice(0, 8));
  }
  // El balón también: perder la pelota de vista es perder el partido de vista.
  mark(w.px[g.ball], w.py[g.ball], ballFill, "");
}

/**
 * Tres puntitos animados sobre un disco: "esta persona está escribiendo".
 *
 * Responde a la pregunta que se hace todo el mundo cuando un jugador se queda
 * clavado en mitad del campo — si se ha caído, si se ha ido, o si está
 * tecleando. Se dibuja en espacio de mundo pero con tamaño compensado por el
 * zoom, igual que el nombre, para que se lea a cualquier distancia de cámara.
 */
function drawTypingDots(
  ctx: CanvasRenderingContext2D, x: number, y: number, zoom: number, t: number,
): void {
  const r = 2.2 / zoom;
  const gap = 6.5 / zoom;
  const baseY = y - 22 / zoom;
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.85)";
  ctx.shadowBlur = 4 / zoom;
  for (let i = 0; i < 3; i++) {
    // Onda desfasada por punto: el clásico "•••" que sube y baja en cadena.
    const phase = t * 6 - i * 0.6;
    const bob = Math.max(0, Math.sin(phase)) * (3 / zoom);
    const alpha = 0.45 + 0.45 * Math.max(0, Math.sin(phase));
    ctx.beginPath();
    ctx.arc(x + (i - 1) * gap, baseY - bob, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
    ctx.fill();
  }
  ctx.restore();
}

/** Cómo pintar cada slot en cancha: color por equipo y nombre por la lista de
 *  jugadores. Local en modo local puro (sin red): un único slot 0. */
interface RenderRoster {
  teamOf: (slot: number) => 0 | 1;
  nameOf: (slot: number) => string;
  localSlot: number;
  localName: string;
  /** Nombres de los dos equipos para el marcador (editables por el admin). */
  teamNames: [string, string];
  /** Escala de render vigente (<1 = el escalador adaptativo la ha bajado). */
  renderScale: number;
  /** Qué enseñar del partido (ajustes de CONFIG). */
  showNames: boolean;
  showFps: boolean;
  /** ¿Se dibujan las estelas? Ajuste del jugador (ver `Settings.trails`). */
  trails: boolean;
  /** ACCESO · suaviza los fogonazos a pantalla completa. */
  reduceFlashes: boolean;
  /** ¿Está este slot escribiendo en el chat? (tres puntitos sobre su disco). */
  typingOf: (slot: number) => boolean;
  /** Icono del disco de un slot (glifo ya resuelto; "" si no tiene). */
  iconOf: (slot: number) => string;
  /**
   * Slot FANTASMA: el servidor mueve un cuerpo ahí, pero en la sala no hay
   * nadie en ese sitio. Solo pasa contra un servidor sin actualizar, que no
   * aparca los slots libres (ver `serverOutdated`). No se dibuja: un sitio
   * vacío no es un jugador, y pintarlo como tal llena la cancha de rivales de
   * mentira. Su cuerpo se sigue simulando igual —eso lo decide el servidor—,
   * así que la pelota puede rebotar en su sitio hasta que el edge se despliegue.
   */
  ghostOf: (slot: number) => boolean;
  /** Reloj de animación (segundos) para los puntitos. */
  clock: number;
}

/**
 * Los surtidores de efectos que el render necesita, en un solo bulto.
 *
 * Iban sueltos como parámetros y eran uno; con cuatro, la firma de `draw` se
 * convierte en una fila de sistemas donde es fácil cruzar dos al llamar. Van
 * juntos porque se dibujan juntos y en un orden que importa.
 */
interface Efectos {
  /** Chispas de patada, blancas y compartidas. */
  particles: ParticleSystem;
  /** Cascotes de gol, uno por equipo (ver dónde se construyen). */
  goalBits: [ParticleSystem, ParticleSystem];
  /** Ondas expansivas: gol y superpatada. */
  rings: RingSystem;
  /** Estelas de los discos, del color de su equipo. */
  trails: TrailSystem;
  /** Estela del balón: aparte, y con fuerza según lo fuerte que vaya. */
  ballTrail: TrailSystem;
}

function draw(ctx: CanvasRenderingContext2D, g: GameState, fps: number, cam: Camera, vw: number, vh: number, dpr: number, fx: Efectos, netLabel: string, roster: RenderRoster): void {
  const { trails, ballTrail } = fx;
  const w = g.world;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = FRAME_BG;
  ctx.fillRect(0, 0, vw, vh);
  const tx = vw / 2 - cam.x * cam.zoom;
  const ty = vh / 2 - cam.y * cam.zoom;
  ctx.setTransform(dpr * cam.zoom, 0, 0, dpr * cam.zoom, dpr * tx, dpr * ty);

  ctx.drawImage(getPitchCanvas(mapById(g.mapId)), -MARGIN, -MARGIN);

  // El saque NO se pinta. La regla sigue ahí (el que marcó no entra al círculo
  // hasta que la pelota está en juego), pero el aro encendido sobre el centro
  // ensuciaba el saque y, al apagarse, parecía que algo se rompía. El círculo
  // central del campo ya marca el sitio; la regla dura dos segundos y solo
  // afecta a quien acaba de marcar, que es justo quien sabe que marcó.

  // ACC-C · Estelas. Se muestrean con las posiciones YA DIBUJADAS —las
  // interpoladas de los rivales incluidas— porque una estela que no salga de
  // debajo del disco que se está viendo no es una estela, es un rastro de otro
  // disco. Y se pintan antes que los discos para quedar debajo.
  if (roster.trails) {
    // El radio sale del primer disco que HAYA en la cancha, no del slot 0: con
    // el slot 0 vacío se colaba el radio de la pelota y las estelas salían
    // enanas sin motivo aparente.
    let radioDisco = 0;
    for (let i = 0; i < g.players.length; i++) {
      const body = g.players[i];
      if (body < 0 || g.active[i] === false || roster.ghostOf(i)) { trails.disable(i); continue; }
      if (radioDisco === 0) radioDisco = w.radius[body];
      // `maxStep` corta la estela cuando el disco salta (saque, gol, reinicio)
      // en vez de moverse: sin esto queda un trazo cruzando la cancha entera.
      trails.sample(i, roster.teamOf(i), w.px[body], w.py[body], w.radius[body] * 4);
    }
    if (radioDisco > 0) trails.draw(ctx, radioDisco);

    // La estela del BALÓN. Va aparte de la de los discos y no por capricho: es
    // del color del balón, y sobre todo NO es constante — crece con la potencia
    // del disparo y se apaga sola cuando el balón frena.
    //
    // La fuerza sale de la RAPIDEZ del balón, no del evento de patada, y esa es
    // la decisión que importa:
    //
    //  - La rapidez ES la potencia. `kick()` la fija en proporción directa a la
    //    carga, así que un disparo a barra llena sale al máximo y un toque flojo
    //    casi no deja rastro, sin tener que llevar la cuenta de nada.
    //  - Se apaga sola con el arrastre, sin temporizador que mantener.
    //  - Y funciona igual con los disparos de los RIVALES en online. Con el
    //    evento habría que enchufarlo también a la reconciliación, y ese es
    //    exactamente el cableado que se queda a medias: la mitad del partido
    //    tendría estela y la otra mitad no.
    const vBola = Math.hypot(w.vx[g.ball], w.vy[g.ball]);
    const fuerza = ballTrailStrength(vBola, maxKickSpeed(g.courtW));
    if (fuerza > 0) {
      ballTrail.sample(0, 0, w.px[g.ball], w.py[g.ball], w.radius[g.ball] * 5, fuerza);
      ballTrail.draw(ctx, w.radius[g.ball]);
    } else {
      ballTrail.disable(0);
    }
  } else {
    trails.clear();
    ballTrail.clear();
  }

  // Todos los discos, con su color de equipo (rojo/turquesa). Un slot VACÍO no
  // se dibuja: los sitios libres de una sala no son jugadores (su cuerpo está
  // aparcado fuera del campo, ver `setSlotActive` en @tak/sim).
  for (let i = 0; i < g.players.length; i++) {
    const body = g.players[i];
    if (body < 0 || g.active[i] === false || roster.ghostOf(i)) continue;
    const style = teamStyle[roster.teamOf(i)] ?? teamStyle[0];
    drawPlayer(ctx, w, body, style, g.chargings[i], g.possession[i], g.kickPulses[i],
      g.kickArmed[i] === true, roster.iconOf(i));
  }
  // Quién está escribiendo (encima de todo lo demás del disco).
  for (let i = 0; i < g.players.length; i++) {
    const body = g.players[i];
    if (body < 0 || g.active[i] === false || roster.ghostOf(i)) continue;
    if (!roster.typingOf(i)) continue;
    drawTypingDots(ctx, w.px[body], w.py[body] - w.radius[body], cam.zoom, roster.clock);
  }
  // Nombres: el propio en blanco, el resto en tono suave.
  if (roster.showNames) for (let i = 0; i < g.players.length; i++) {
    const body = g.players[i];
    if (body < 0 || g.active[i] === false || roster.ghostOf(i)) continue;
    const isLocal = i === roster.localSlot;
    const name = isLocal ? roster.localName : roster.nameOf(i);
    if (!name) continue;
    drawNameTag(ctx, w.px[body], w.py[body] - w.radius[body], name, cam.zoom, isLocal ? "#ffffff" : "rgba(230,245,255,0.8)");
  }

  ctx.save();
  ctx.shadowColor = "rgba(230,245,255,0.75)";
  ctx.shadowBlur = 7;
  ctx.beginPath();
  ctx.arc(w.px[g.ball], w.py[g.ball], w.radius[g.ball], 0, Math.PI * 2);
  ctx.fillStyle = ballFill;
  ctx.fill();
  ctx.restore();
  ctx.beginPath();
  ctx.arc(w.px[g.ball], w.py[g.ball], w.radius[g.ball], 0, Math.PI * 2);
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(10,20,28,0.55)";
  ctx.stroke();

  // Cascotes y chispas por encima de los discos; las ONDAS por debajo de nada,
  // que es donde tienen que ir: un aro tapando el disco al que sigue esconde
  // justo lo que se está mirando.
  fx.goalBits[0].draw(ctx);
  fx.goalBits[1].draw(ctx);
  fx.particles.draw(ctx);
  fx.rings.draw(ctx);

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // Flechas de borde para quien se ha salido de cuadro (jugadores y balón).
  // Van en espacio de PANTALLA, así que después de todo lo que va en mundo.
  drawEdgeMarkers(ctx, g, cam, vw, vh, dpr, roster);

  if (g.goalAnim > 0) {
    // `goalSide === 0` = el balón entró por la portería DERECHA (marcó el rojo);
    // `1` = entró por la izquierda. El láser sale de ahí, que es donde acaba de
    // reventar la explosión de la cancha: las dos cuentan lo mismo.
    drawGoalAnim(
      ctx, g.goalAnim, g.goalSide === 0 ? ACCENT_RED : ACCENT_TEAL,
      vw, vh, roster.reduceFlashes, g.goalSide === 1 ? -1 : 1,
    );
  }

  drawHud(ctx, g, fps, vw, netLabel, roster.teamNames, roster.renderScale, roster.showFps);
}

function fmtClock(s: number): string {
  const t = Math.max(0, Math.floor(s));
  return `${Math.floor(t / 60)}:${(t % 60).toString().padStart(2, "0")}`;
}

/**
 * Recorta un texto al ancho dado, con puntos suspensivos si no cabe.
 *
 * El resultado se cachea: `measureText` en un bucle es de lo más caro que se
 * puede hacer en un canvas, y los nombres de equipo cambian una vez cada mucho
 * mientras esto se dibuja 60 veces por segundo.
 */
const ellipsisCache = new Map<string, string>();
function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  const key = `${ctx.font}|${Math.round(maxW)}|${text}`;
  const hit = ellipsisCache.get(key);
  if (hit !== undefined) return hit;
  let out = text;
  if (ctx.measureText(text).width > maxW) {
    let t = text;
    while (t.length > 1 && ctx.measureText(t + "…").width > maxW) t = t.slice(0, -1);
    out = t + "…";
  }
  if (ellipsisCache.size > 64) ellipsisCache.clear(); // techo: no es una fuga
  ellipsisCache.set(key, out);
  return out;
}

/** Gradientes del marcador, cacheados por geometría (dependen solo del ancho). */
let scoreTintKey = "";
let scoreTintL: CanvasGradient | null = null;
let scoreTintR: CanvasGradient | null = null;

/**
 * Marcador SIMÉTRICO: la composición se construye a partir del centro de la
 * pantalla hacia fuera, con medidas espejadas, en vez de medir el conjunto y
 * centrarlo. La diferencia importa: midiendo el conjunto, un nombre de equipo
 * largo desplaza los números y el eje del marcador deja de coincidir con el eje
 * de la cancha. Aquí el separador cae SIEMPRE en el centro exacto, y cada bando
 * dispone de la misma anchura reservada para su nombre; lo que no cabe se
 * recorta con puntos suspensivos en vez de empujar al resto.
 *
 *                     │◄── reservado ──►│   │◄── reservado ──►│
 *          NOMBRE ROJO│               2 │ · │ 1               │TURQUESA
 *                                    1:23 · OBJETIVO 3
 */
function drawScoreboard(
  ctx: CanvasRenderingContext2D, g: GameState, vw: number, teamNames: [string, string],
): void {
  const cx = Math.round(vw / 2);
  // En pantallas estrechas todo el bloque encoge de forma proporcional, para que
  // el marcador no invada la cancha ni se solape consigo mismo.
  const k = Math.min(1, vw / 760);
  // Tipografía del sistema de diseño: display CURSIVA para los números (la
  // misma del menú y del panel de pausa) y monoespaciada para los microdatos.
  // Antes el HUD usaba la fuente de interfaz redonda y era el único sitio del
  // juego que hablaba otro idioma visual.
  const NUM_FONT = `italic 900 ${Math.round(34 * k)}px ${DISPLAY_STACK}`;
  const NAME_FONT = `800 ${Math.round(12 * k)}px ${UI_STACK}`;
  const META_FONT = `700 ${Math.round(11 * k)}px ${MONO_STACK}`;

  // Geometría espejada: todo se mide desde el centro.
  const halfSep = Math.round(11 * k);       // medio hueco del separador central
  const numW = Math.round(36 * k);          // ancho reservado a cada marcador
  const nameGap = Math.round(14 * k);       // aire entre número y nombre
  const nameW = Math.round(132 * k);        // ancho reservado a cada nombre
  const padX = Math.round(20 * k);
  const halfPanel = halfSep + numW + nameGap + nameW + padX;
  const panelH = Math.round(64 * k);
  const yScore = Math.round(27 * k);        // eje vertical de números y nombres
  const yMeta = Math.round(48 * k);         // reloj y objetivo, debajo

  // Panel achaflanado: cuelga del borde superior y se corta a 45° abajo, como
  // cualquier otra superficie del juego (`.tb-cut`). Nada de esquinas redondas.
  const cut = Math.round(18 * k);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(cx - halfPanel, 0);
  ctx.lineTo(cx + halfPanel, 0);
  ctx.lineTo(cx + halfPanel, panelH - cut);
  ctx.lineTo(cx + halfPanel - cut, panelH);
  ctx.lineTo(cx - halfPanel + cut, panelH);
  ctx.lineTo(cx - halfPanel, panelH - cut);
  ctx.closePath();
  ctx.clip();

  ctx.fillStyle = "rgba(6,10,15,0.82)";
  ctx.fillRect(cx - halfPanel, 0, halfPanel * 2, panelH);
  // Tinte de equipo por mitad, espejado: cada bando "posee" su lado.
  // Los gradientes solo dependen de la geometría del panel: crearlos en cada
  // frame es asignar dos objetos de GPU 60 veces por segundo para nada.
  const key = `${cx}|${halfPanel}`;
  if (key !== scoreTintKey || !scoreTintL || !scoreTintR) {
    scoreTintKey = key;
    scoreTintL = ctx.createLinearGradient(cx - halfPanel, 0, cx, 0);
    scoreTintL.addColorStop(0, "rgba(255,46,99,0)");
    scoreTintL.addColorStop(1, "rgba(255,46,99,0.2)");
    scoreTintR = ctx.createLinearGradient(cx + halfPanel, 0, cx, 0);
    scoreTintR.addColorStop(0, "rgba(34,230,255,0)");
    scoreTintR.addColorStop(1, "rgba(34,230,255,0.2)");
  }
  ctx.fillStyle = scoreTintL;
  ctx.fillRect(cx - halfPanel, 0, halfPanel, panelH);
  ctx.fillStyle = scoreTintR;
  ctx.fillRect(cx, 0, halfPanel, panelH);
  // Textura de fibra: rayas diagonales finísimas, la misma idea que `.tb-carbon`.
  ctx.globalAlpha = 0.05;
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = cx - halfPanel - panelH; x < cx + halfPanel; x += Math.max(3, Math.round(4 * k))) {
    ctx.moveTo(x, panelH);
    ctx.lineTo(x + panelH, 0);
  }
  ctx.stroke();
  ctx.globalAlpha = 1;
  // Filo inferior de neón en dos mitades (el "spine" del sistema de diseño).
  const edge = Math.max(1, Math.round(2 * k));
  ctx.fillStyle = ACCENT_RED;
  ctx.globalAlpha = 0.75;
  ctx.fillRect(cx - halfPanel, panelH - edge, halfPanel, edge);
  ctx.fillStyle = ACCENT_TEAL;
  ctx.fillRect(cx, panelH - edge, halfPanel, edge);
  ctx.globalAlpha = 1;
  ctx.restore();

  ctx.textBaseline = "middle";

  // Cuña central: el eje de simetría, un rombo del color de quien va ganando
  // (gris en empate). Sustituye a los dos puntos, que eran tipografía prestada.
  const lead = g.score[0] === g.score[1] ? null : (g.score[0] > g.score[1] ? 0 : 1);
  const wedge = Math.round(5 * k);
  ctx.save();
  ctx.translate(cx, yScore);
  ctx.beginPath();
  ctx.moveTo(0, -wedge * 1.9);
  ctx.lineTo(wedge, 0);
  ctx.lineTo(0, wedge * 1.9);
  ctx.lineTo(-wedge, 0);
  ctx.closePath();
  ctx.fillStyle = lead === null ? "rgba(160,180,196,0.5)" : (lead === 0 ? ACCENT_RED : ACCENT_TEAL);
  ctx.fill();
  ctx.restore();

  // Marcadores, cada uno pegado a su lado del separador. El del bando que va
  // ganando lleva su halo: se lee quién domina sin comparar dos cifras.
  ctx.font = NUM_FONT;
  ctx.save();
  ctx.textAlign = "right";
  ctx.fillStyle = ACCENT_RED;
  if (lead === 0) { ctx.shadowColor = ACCENT_RED; ctx.shadowBlur = 12 * k; }
  ctx.fillText(String(g.score[0]), cx - halfSep, yScore);
  ctx.restore();
  ctx.save();
  ctx.textAlign = "left";
  ctx.fillStyle = ACCENT_TEAL;
  if (lead === 1) { ctx.shadowColor = ACCENT_TEAL; ctx.shadowBlur = 12 * k; }
  ctx.fillText(String(g.score[1]), cx + halfSep, yScore);
  ctx.restore();

  // Nombres de equipo, en su anchura reservada (recortados si hace falta).
  ctx.font = NAME_FONT;
  ctx.textAlign = "right";
  ctx.fillStyle = ACCENT_RED;
  ctx.fillText(ellipsize(ctx, teamNames[0].toUpperCase(), nameW), cx - halfSep - numW - nameGap, yScore);
  ctx.textAlign = "left";
  ctx.fillStyle = ACCENT_TEAL;
  ctx.fillText(ellipsize(ctx, teamNames[1].toUpperCase(), nameW), cx + halfSep + numW + nameGap, yScore);

  // Reloj y objetivo, centrados bajo el marcador (una sola línea, simétrica).
  // En PRÓRROGA el reloj cuenta hacia ARRIBA desde el límite: restar daría un
  // tiempo negativo, y sobre todo mentiría — ya no queda tiempo, se juega hasta
  // el gol de oro. Se dice eso mismo, y en el color de "esto es ahora o nunca".
  ctx.textAlign = "center";
  ctx.font = META_FONT;
  if (g.overtime) {
    ctx.fillStyle = ACCENT_RED;
    ctx.fillText(`PRÓRROGA · GOL DE ORO  ·  ${fmtClock(g.matchTime - g.rules.timeLimit)}`, cx, yMeta);
  } else {
    const clock = g.rules.timeLimit > 0 ? fmtClock(g.rules.timeLimit - g.matchTime) : fmtClock(g.matchTime);
    const meta = g.rules.goalLimit > 0 ? `${clock}  ·  OBJETIVO ${g.rules.goalLimit}` : clock;
    ctx.fillStyle = "#7f93a5";
    ctx.fillText(meta, cx, yMeta);
  }
}

/**
 * GP-2 · Cuenta atrás previa al saque, sobre la cancha.
 *
 * El número lo manda el servidor, así que los dos jugadores ven el mismo y
 * empiezan a la vez. Late una vez por segundo (el resto del segundo se encoge)
 * para que se sienta el ritmo sin mirar los decimales.
 */
function drawCountdown(ctx: CanvasRenderingContext2D, vw: number, vh: number, secs: number): void {
  const n = Math.max(1, Math.ceil(secs));
  const frac = n - secs; // 0 justo al cambiar de número, →1 al final del segundo
  const pop = 1 + 0.35 * Math.max(0, 1 - frac * 4);
  const cx = vw / 2;
  const cy = vh / 2;
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.globalAlpha = 0.85;
  ctx.font = `900 ${Math.round(140 * pop)}px "Segoe UI",system-ui,sans-serif`;
  ctx.fillStyle = "rgba(8,12,17,0.55)";
  ctx.fillText(String(n), cx, cy + 4);
  ctx.fillStyle = "#eef4f8";
  ctx.fillText(String(n), cx, cy);
  ctx.globalAlpha = 0.75;
  ctx.font = '700 18px "Segoe UI",system-ui,sans-serif';
  ctx.fillStyle = ACCENT_TEAL;
  ctx.fillText("PREPARADOS", cx, cy + 100 * pop);
  ctx.restore();
}

function drawHud(
  ctx: CanvasRenderingContext2D, g: GameState, fps: number, vw: number,
  netLabel = "", teamNames: [string, string] = [...DEFAULT_TEAM_NAMES] as [string, string],
  renderScale = 1, showFps = true,
): void {
  drawScoreboard(ctx, g, vw, teamNames);

  ctx.textAlign = "start";
  ctx.textBaseline = "top";
  // Si el escalador adaptativo ha bajado la resolución para sostener los fps,
  // se dice: si no, el jugador ve la imagen menos nítida sin saber por qué y no
  // hay forma de distinguir "va justo de GPU" de "va justo de CPU".
  if (showFps) {
    const scaled = renderScale < 1 ? `  ·  ${Math.round(renderScale * 100)}% RES` : "";
    ctx.fillStyle = renderScale < 1 ? "#ffc93c" : "#8fa3b5";
    ctx.font = "14px monospace";
    ctx.fillText(`${fps} FPS${scaled}`, 8, 66);
  }

  // Indicador de netcode (predicción + reconciliación; loopback o servidor real).
  if (netLabel) {
    ctx.textAlign = "end";
    ctx.fillStyle = ACCENT_TEAL;
    ctx.font = "bold 12px monospace";
    ctx.fillText("● " + netLabel, vw - 8, 66);
    ctx.textAlign = "start";
  }
}

/**
 * Dibuja el fondo vivo del menú: la cancha, los discos y el balón de la partida
 * de ambiente. Sin HUD ni nombres — es un plano de fondo, y encima va el menú.
 * El desenfoque lo pone el CSS (`canvas#game.tak-menu-backdrop`), que es gratis
 * en GPU comparado con difuminar a mano en el canvas.
 */
function drawBackdrop(
  ctx: CanvasRenderingContext2D, g: GameState, cam: Camera,
  vw: number, vh: number, dpr: number,
): void {
  const w = g.world;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = FRAME_BG;
  ctx.fillRect(0, 0, vw, vh);
  const tx = vw / 2 - cam.x * cam.zoom;
  const ty = vh / 2 - cam.y * cam.zoom;
  ctx.setTransform(dpr * cam.zoom, 0, 0, dpr * cam.zoom, dpr * tx, dpr * ty);
  ctx.drawImage(getPitchCanvas(mapById(g.mapId)), -MARGIN, -MARGIN);
  for (let i = 0; i < g.players.length; i++) {
    const body = g.players[i];
    if (body < 0 || g.active[i] === false) continue;
    const style = teamStyle[g.teams[i]] ?? teamStyle[0];
    drawPlayer(ctx, w, body, style, false, 0, 0, false);
  }
  ctx.save();
  ctx.shadowColor = "rgba(230,245,255,0.75)";
  ctx.shadowBlur = 7;
  ctx.beginPath();
  ctx.arc(w.px[g.ball], w.py[g.ball], w.radius[g.ball], 0, Math.PI * 2);
  ctx.fillStyle = ballFill;
  ctx.fill();
  ctx.restore();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

/** Mini-gráfico de ping (sparkline) abajo a la derecha: RTT actual + máximo.
 *  Color por umbral (verde <60 ms · ámbar <120 · rojo peor). Se dibuja en espacio
 *  de pantalla (px CSS), tras el mundo. */
function drawPingGraph(
  ctx: CanvasRenderingContext2D, vw: number, vh: number, dpr: number,
  history: number[], cur: number, max: number, measured = true, jitter = 0,
): void {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const W = 122, H = 46, m = 12;
  // En móvil esa esquina es el botón de patada, así que el medidor se sube lo
  // que ocupa. En escritorio la esquina está libre: el botón de FEEDBACK ya no
  // sale en partida.
  const reservado = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches ? 120 : 0;
  const x0 = vw - W - m, y0 = vh - H - m - reservado;
  const col = cur < 60 ? ACCENT_TEAL : cur < 120 ? "#ffc93c" : ACCENT_RED;

  ctx.beginPath();
  ctx.roundRect(x0, y0, W, H, 9);
  ctx.fillStyle = "rgba(5,8,12,0.8)";
  ctx.fill();
  ctx.strokeStyle = "rgba(120,150,170,0.18)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  ctx.font = "700 8px system-ui, sans-serif";
  ctx.fillStyle = "#6d8092";
  ctx.fillText("PING", x0 + 10, y0 + 13);
  // ESTABILIDAD, arriba a la derecha del cuadro. Es la mitad de la historia que
  // faltaba: 110 ms constantes se juegan como una LAN, y 110 ms que saltan a 360
  // son tirones. Sin este número, "110 ms" no distingue "vivo lejos" —que no
  // tiene arreglo— de "mi Wi-Fi va mal" —que se arregla con un cable—.
  if (measured) {
    const j = Math.round(jitter);
    const jc = j < 15 ? "#3affd4" : j < 40 ? "#ffc93c" : ACCENT_RED;
    ctx.textAlign = "right";
    ctx.fillStyle = jc;
    ctx.fillText(j < 15 ? "ESTABLE" : j < 40 ? `±${j}` : `±${j} INESTABLE`, x0 + W - 10, y0 + 13);
    ctx.textAlign = "left";
    ctx.fillStyle = "#6d8092";
  }

  // Sparkline de los últimos N valores.
  const gx = x0 + 10, gw = W - 20, gy = y0 + 16, gh = H - 30;
  const scale = Math.max(60, max, ...history);
  if (history.length >= 2) {
    ctx.beginPath();
    for (let i = 0; i < history.length; i++) {
      const px = gx + gw * (i / (history.length - 1));
      const py = gy + gh - (Math.min(history[i], scale) / scale) * gh;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";
    ctx.stroke();
  }

  ctx.textBaseline = "bottom";
  ctx.textAlign = "left";
  ctx.font = "bold 12px monospace";
  // Sin medida todavía, "0 ms" mentiría: es indistinguible de una conexión
  // local perfecta, y es justo el caso en el que algo va mal (los inputs no se
  // están confirmando). Se marca como lo que es: sin dato.
  ctx.fillStyle = measured ? col : "#6d8092";
  ctx.fillText(measured ? `${Math.round(cur)}ms` : "— ms", x0 + 10, y0 + H - 6);
  ctx.textAlign = "right";
  ctx.fillStyle = "#6d8092";
  ctx.font = "10px monospace";
  ctx.fillText(`max ${Math.round(max)}`, x0 + W - 10, y0 + H - 6);
  ctx.textAlign = "start";
  ctx.textBaseline = "top";
}

/**
 * Estilo de equipo vigente (color + marca de forma). Es mutable porque la marca
 * se activa/desactiva en caliente desde ACCESO y el render lo lee en cada frame;
 * recrear el pipeline de dibujo por eso sería absurdo. El COLOR ya no depende de
 * ningún ajuste: la corrección de daltonismo es un filtro sobre la imagen
 * entera (ver `a11y.ts`), no una paleta alternativa.
 */
let teamStyle: [TeamStyle, TeamStyle] = teamStyles(true);
const ballFill = BALL_COLOR;

// Endpoints por región (TAREA 3): cada RegionId → su wss://… (ver lobby/regions).
// Con VITE_REGION_ENDPOINTS se despliega multi-región; VITE_NETPLAY_URL sigue
// valiendo como 1 región (compat). Sin backend configurado → loopback offline.
const ENV_RAW = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
// WebTransport (UDP/QUIC) legacy: solo si se pasa un https:// suelto y NO hay
// registro multi-región (ese caso usa WebSocket por región).
const WT_URL = (!ENV_RAW.VITE_REGION_ENDPOINTS && ENV_RAW.VITE_NETPLAY_URL?.startsWith("https://"))
  ? ENV_RAW.VITE_NETPLAY_URL
  : undefined;
const NET_LATENCY_TICKS = 3; // ~50 ms one-way (solo loopback)
// El delay de interpolación ahora lo decide el cliente (adaptativo al jitter):
// ver `PredictiveClient.interpDelayTicks`.

/** Sesión de red unificada: el bucle no distingue loopback de WebSocket real. */
interface Netplay {
  readonly client: PredictiveClient;
  step(input: InputState): void;
  dispose(): void;
  readonly status: string;
  /** Id de sala asignado por el servidor (para el link de invitación), o null. */
  readonly roomId: string | null;
  /** Fase de la sala (lobby/countdown/playing/paused/over). */
  readonly phase: RoomPhase;
  /** Segundos que faltan para el saque en `countdown` (GP-2), según el servidor. */
  readonly countdown: number;
  /** Ocupantes actuales de la sala (slot + nombre). */
  readonly peers: PeerInfo[];
  /** Quién está MIRANDO la sala y dio nombre (sin slot ni cuerpo). */
  readonly spectators: string[];
  /** Cuánta gente mira en total (el fondo del menú de otros cuenta, y es anónimo). */
  readonly spectatorCount: number;
  /** Nombres de los equipos de la sala (los fija el admin; iguales para todos). */
  readonly teamNames: [string, string];
  /** Envía una acción de control (solo la aplica el servidor si eres admin). */
  control(action: ControlAction, opts?: ControlOpts): void;
  /** Reporta el RTT medido (ms) al servidor (ping por usuario). No-op offline. */
  reportPing(ms: number): void;
  /** Manda un mensaje de chat a la sala. `false` = no salió (offline/sin
   *  conexión) y quien llama debe pintarlo por su cuenta. */
  sendChat(text: string): boolean;
  /** Llegó chat de la sala (incluido el eco del propio mensaje). */
  onChat: ((line: ChatLine) => void) | null;
  /** NET-4 · El servidor anuncia un reinicio con cuenta atrás. */
  onNotice: ((n: { seconds: number; message?: string }) => void) | null;
  /** El admin de la sala nos expulsó: hay que sacar a la persona del partido. */
  onKicked: (() => void) | null;
  /** Anuncia a la sala que este jugador está escribiendo (o dejó de hacerlo). */
  setTyping(on: boolean): void;
  /** Cambia el nombre visible y/o el icono del disco en caliente. */
  setProfile(p: { name?: string; icon?: string }): void;
  /** SEC-3 · Reporta a un ocupante de la sala. `false` = no salió. */
  report(targetSlot: number, reason: ReportReason): boolean;
  /** Fuerza un intento de reconexión (botón de la UI). No-op si no aplica. */
  retryNow(): void;
  /** Rehace el saludo con la sala cuando la partida no avanza. No-op offline. */
  forceRejoin(): void;
}

// Intención de la conexión online, decidida en el lobby.
type OnlineIntent =
  | { kind: "quick"; region?: string }
  | { kind: "join"; roomId: string; password?: string; region?: string }
  | { kind: "create"; name?: string; password?: string; listed?: boolean; region?: string; mapId?: string; goalLimit?: number; timeLimit?: number; capacity?: number; autoStart?: boolean };

/** Construye la URL del WebSocket con los parámetros de la intención. */
function buildWsUrl(base: string, intent: OnlineIntent, playerName = ""): string {
  const p = new URLSearchParams();
  // Nombre visible del jugador: viaja en toda conexión (rápida / crear / unir)
  // para poblar la lista de jugadores y la etiqueta del rival en cancha.
  if (playerName) p.set("pname", playerName);
  // ACC-1: id de invitado anónimo del dispositivo. No autentica nada; sirve para
  // correlacionar logs/errores del mismo navegador entre partidas.
  p.set("gid", getGuestId());
  // Id de PESTAÑA (no de dispositivo): con él el servidor distingue "yo volviendo
  // tras una caída" de "otra ventana mía". Ver la nota en identity.ts.
  p.set("sid", getSessionId());
  // ACC-B · Pase de cuenta, si hay sesión. El edge comprueba la firma en local.
  //
  // Se lee de forma SÍNCRONA porque esta función se llama en CADA intento de
  // reconexión y ahí no se puede esperar. Si el pase caducó, `ticketNow`
  // devuelve vacío y pide uno nuevo para la próxima: se conecta como invitado en
  // vez de no conectarse, que es la degradación correcta — perder el icono es
  // molesto, perder la partida es inaceptable.
  const pase = ticketNow();
  if (pase) p.set("tok", pase);
  if (intent.kind === "join") {
    p.set("room", intent.roomId);
    if (intent.password) p.set("pw", intent.password);
  } else if (intent.kind === "create") {
    p.set("create", "1");
    if (intent.name) p.set("name", intent.name);
    if (intent.password) p.set("pw", intent.password);
    p.set("listed", intent.listed === false ? "0" : "1");
    if (intent.region) p.set("region", intent.region);
    if (intent.mapId) p.set("map", intent.mapId);
    if (intent.goalLimit != null) p.set("goal", String(intent.goalLimit));
    if (intent.timeLimit != null) p.set("time", String(intent.timeLimit));
    if (intent.capacity != null) p.set("cap", String(intent.capacity));
    // La sala nace ya EN JUEGO: sin esto había que conectar, esperar el estado
    // "lobby" y pedir `start` — una vuelta entera a la red antes de poder jugar.
    if (intent.autoStart) p.set("start", "1");
  }
  const qs = p.toString();
  return qs ? base + (base.includes("?") ? "&" : "?") + qs : base;
}

function createNetplay(
  map: MapConfig,
  rules: MatchRules,
  intent: OnlineIntent = { kind: "quick" },
  onReject?: (reason: string) => void,
  onReady?: () => void,
  onState?: (phase: RoomPhase) => void,
  onPeers?: (peers: PeerInfo[]) => void,
  playerName = "",
  onConnState?: (state: ConnState, attempt: number) => void,
  // Nombres de equipo del modo OFFLINE: ahí no hay sala que los fije, así que
  // salen de los ajustes locales y pueden cambiar en mitad de la partida.
  localTeamNames: () => [string, string] = () => [...DEFAULT_TEAM_NAMES] as [string, string],
): Netplay {
  const opts = { players: 2 as const, localSlot: 0 as const, map, rules };
  // WebTransport (UDP/QUIC) legacy de 1 región.
  if (WT_URL) {
    let host = WT_URL;
    try { host = new URL(WT_URL).host; } catch { /* url tal cual */ }
    const wt = new WtNetClient(WT_URL, opts);
    return {
      client: wt.client,
      step: (i) => wt.send(i),
      dispose: () => wt.close(),
      get status() { return wt.connected ? `ONLINE-UDP · ${host}` : "CONECTANDO…"; },
      roomId: null,
      phase: "playing",
      countdown: 0,
      peers: [],
      spectators: [],
      spectatorCount: 0,
      teamNames: [...DEFAULT_TEAM_NAMES] as [string, string],
      control: () => { /* WebTransport MVP: sin ciclo de vida de sala */ },
      reportPing: () => { /* WebTransport MVP: sin roster de sala */ },
      sendChat: () => false, // WebTransport MVP: sin chat de sala → eco local
      onChat: null,
      onNotice: null,
      onKicked: null,
      setTyping: () => { /* WebTransport MVP: sin roster de sala */ },
      setProfile: () => { /* WebTransport MVP: sin roster de sala */ },
      report: () => false, // WebTransport MVP: sin moderación de sala
      retryNow: () => { /* WebTransport MVP: sin reconexión automática */ },
      forceRejoin: () => { /* WebTransport MVP: sin reconexión automática */ },
    };
  }
  // WebSocket por REGIÓN: resuelve el edge de la región de la intención (o el
  // por defecto). El servidor de esa región hostea la sala → el ping es real.
  const region = intent.region;
  const wsBase = regionWsUrl(region);
  if (wsBase) {
    let host = wsBase;
    try { host = new URL(wsBase).host; } catch { /* url tal cual */ }
    const label = region ? `${region} · ${host}` : host;
    // La URL se recalcula en CADA intento: tras la primera bienvenida ya sabemos
    // el id de sala, así que una reconexión vuelve a LA MISMA sala en vez de
    // crear una nueva o caer en el emparejamiento rápido (UX-2/NET-1).
    const urlFor = (roomId: string | null): string => {
      const target: OnlineIntent = roomId
        ? { kind: "join", roomId, password: intent.kind === "create" ? intent.password : intent.kind === "join" ? intent.password : undefined, region }
        : intent;
      return buildWsUrl(wsBase, target, playerName);
    };
    const ws = new WsNetClient(urlFor, opts);
    if (onReject) ws.onRejected = onReject;
    if (onReady) ws.onWelcome = onReady;
    if (onState) ws.onState = onState;
    if (onPeers) ws.onPeers = onPeers;
    if (onConnState) ws.onConnState = onConnState;
    // Estado inicial del socket (la conexión ya arrancó en el constructor).
    onConnState?.(ws.connState, ws.retryAttempt);
    const statusFor = (state: ConnState): string =>
      state === "connected" ? `ONLINE · ${label}`
        : state === "reconnecting" ? "RECONECTANDO…"
          : state === "connecting" ? "CONECTANDO…"
            : "SIN CONEXIÓN";
    const np: Netplay = {
      client: ws.client,
      step: (i) => ws.send(i),
      dispose: () => ws.close(),
      get status() { return statusFor(ws.connState); },
      get roomId() { return ws.roomId; },
      get phase() { return ws.phase; },
      get countdown() { return ws.countdown; },
      get peers() { return ws.peers; },
      get spectators() { return ws.spectators; },
      get spectatorCount() { return ws.spectatorCount; },
      get teamNames() { return ws.teamNames; },
      control: (a, o) => ws.control(a, o),
      reportPing: (ms) => ws.reportPing(ms),
      sendChat: (t) => ws.sendChat(t),
      onChat: null,
      onNotice: null,
      onKicked: null,
      setTyping: (on) => ws.setTyping(on),
      setProfile: (p) => ws.setProfile(p),
      report: (slot, reason) => ws.report(slot, reason),
      retryNow: () => ws.retryNow(),
      forceRejoin: () => ws.forceRejoin(),
    };
    // El consumidor engancha `np.onChat` después de construir; se reenvía en
    // vez de exponer el WsNetClient para que main.ts siga hablando solo con
    // `Netplay` (el transporte WT/loopback no tiene chat de sala).
    ws.onChat = (line) => np.onChat?.(line);
    ws.onNotice = (n) => np.onNotice?.(n);
    ws.onKicked = () => np.onKicked?.();
    return np;
  }
  // Offline (sin servidor): loopback in-process con un CPU en el slot 1 para
  // practicar en solitario. El multijugador humano vs humano requiere el
  // servidor WS real (VITE_NETPLAY_URL); ahí NO hay bots.
  const sess = new LoopbackSession(NET_LATENCY_TICKS, { players: 2, bot: true, map, rules });
  const loopbackPeers: PeerInfo[] = [
    { slot: 0, name: playerName || "Tú" },
    { slot: 1, name: "CPU" },
  ];
  return {
    client: sess.client,
    step: (i) => sess.advance(i),
    dispose: () => { /* loopback in-process: nada que cerrar */ },
    get status() { return "OFFLINE · CPU ~50ms"; },
    roomId: null,
    phase: "playing",
    countdown: 0, // el loopback no tiene ciclo de vida de sala
    peers: loopbackPeers,
    spectators: [],
      spectatorCount: 0,
    // Offline los nombres de equipo son ajuste local (no hay sala que los fije).
    get teamNames() { return localTeamNames(); },
    control: () => { /* loopback offline: sin ciclo de vida de sala */ },
    reportPing: () => { /* loopback offline: sin roster de sala */ },
    sendChat: () => false, // no hay sala remota: el mensaje se pinta localmente
    onChat: null,
    onNotice: null,
    onKicked: null,
    setTyping: () => { /* loopback offline: no hay a quién anunciárselo */ },
    setProfile: () => { /* loopback offline: el roster es de mentira */ },
    report: () => false, // no hay sala remota a la que reportar
    retryNow: () => { /* loopback in-process: no hay conexión que reintentar */ },
    forceRejoin: () => { /* loopback in-process: no hay sala remota */ },
  };
}

function main(): void {
  // OBS-1: los errores del cliente van al mismo pipeline de logs que los del
  // servidor. Se instala lo primero, para capturar también los fallos de arranque.
  installClientErrorReporting();
  const [canvas, ctx] = setupCanvas();
  const input = createInputState();
  const audio = new AudioEngine();
  // Los ajustes se leen ANTES de enganchar el teclado: el mapa de teclas es uno
  // de ellos (MOB-3), así que quien haya remapeado sus controles los tiene desde
  // la primera pulsación y no desde el primer cambio de ajustes.
  const settings: Settings = loadSettings();
  const ui = new UIManager(settings);
  const keyboard = attachInput(window, input, () => ui.isTyping(), settings.keybinds);

  // ACC-B · La cuenta, si este despliegue tiene servicio de identidad.
  //
  // Se lee la vuelta del proveedor AQUÍ, lo primero, porque `readAuthOutcome`
  // limpia la URL: dejar `?auth_ok=in` puesto haría que recargar repitiera el
  // aviso para siempre y que ese parámetro acabara en cualquier enlace que se
  // comparta.
  //
  // Pero NO se actúa todavía. El portal de "elige tu nombre" se abre al final de
  // este arranque, va por encima (z-22) y al cerrarse repinta el menú: pintar
  // aquí la pantalla de nick era pintarla debajo de algo que la iba a borrar.
  // Se aplica en `aplicarVuelta`, ya con el portal cerrado.
  //
  // La consulta sí se lanza ya y en segundo plano: no bloquea el arranque, y si
  // el servicio de identidad no contesta el juego sigue funcionando de invitado.
  // Esa es la razón entera de que los edges no dependan de él.
  const vuelta = account.readAuthOutcome();
  const cuentaCargada = account.load();
  // ACC-C · El icono va con la CUENTA, y la cuenta llega por la red. La interfaz
  // ya se construyó con lo que había en este navegador, así que en cuanto se
  // sabe quién eres hay que releer: si no, quien vuelve con la sesión abierta
  // sale a la cancha con el icono de invitado hasta la siguiente recarga.
  // Va aparte de `aplicarVuelta` porque esto pasa SIEMPRE, no solo al volver de
  // un proveedor.
  void cuentaCargada.then(() => ui.syncProfile());
  const aplicarVuelta = (): void => {
    if (vuelta.kind === "none") return;
    void cuentaCargada.then(() => {
      if (vuelta.kind === "nuevo") {
        // Cuenta nueva: falta el nick, y hasta que lo elija no existe nada.
        ui.renderNickname(vuelta.suggestion);
      } else if (vuelta.kind === "in" || vuelta.kind === "linked") {
        ui.openProfile(vuelta.kind === "linked" ? "Proveedor vinculado." : "Sesión iniciada.");
      } else if (vuelta.kind === "error") {
        // El motivo se dice EN el perfil, que es donde acaba de aterrizar, y no
        // en un aviso flotante: aquí hace falta que lo lea, no que lo vea pasar.
        ui.openProfile(account.mensajeDe(vuelta.reason));
      }
    });
  };
  // Escala de texto y filtro de color (accesibilidad), aplicados al documento.
  applyUiScale(settings.uiScale);
  applyVision(settings);
  // MOB-1 · Mando táctil. Rellena el MISMO `InputState` que el teclado, así que
  // desde aquí abajo el juego no distingue un pulgar de una tecla. Arranca
  // oculto; el bucle lo muestra solo mientras se está jugando de verdad.
  const touch = attachTouchControls(input, {
    isBlocked: () => ui.isTyping(),
    // En móvil no hay tecla Intro ni teclas 1/2/3: el chat y el zoom necesitan
    // botón propio o no existen. Van en el overlay táctil porque solo tienen
    // sentido cuando se está jugando con el pulgar.
    onChat: () => ui.openChatFromTouch(),
    onZoom: (delta) => setZoom(cam.zoomIndex + delta),
  });
  /** ¿Toca enseñar el mando táctil ahora mismo? */
  const touchWanted = (): boolean =>
    shouldShowTouch(settings.touchControls, detectPointerEnv());

  const rulesFromSettings = () => ({ goalLimit: settings.goalLimit, timeLimit: settings.timeLimit });
  let game = createGame(mapById(settings.mapId), rulesFromSettings());
  // El color de cada disco lo decide el equipo (de la lista de jugadores online,
  // o de ui.getLocalTeam() en local) en el momento de dibujar; ya no se cachea.
  let playerName = ""; // nombre visible en partida (elegido en la pantalla de entrada)
  let resultShown = false;

  const resetInput = (): void => {
    input.up = input.down = input.left = input.right = input.kick = false;
    input.kickRelease = false;
  };

  // Sesión de red: null = partida local; instancia = partida online autoritativa
  // (loopback in-process o WebSocket real según VITE_NETPLAY_URL). Lo que se
  // renderiza es netplay.client.game, el estado PREDICHO. Ver packages/sim/src/net.
  let netplay: Netplay | null = null;
  let mode: "local" | "online" = "local";
  let roomRegion: string | undefined; // región (edge) de la sala online actual
  /** Contraseña de la sala actual (viaja DENTRO del enlace de invitación). */
  let roomPassword: string | undefined;
  /** Nº de conexiones online de esta pestaña (ámbito provisional del chat). */
  let onlineSession = 0;
  let renderClock = 0; // reloj de interpolación en espacio de ticks del servidor

  // El no-admin ve un overlay de estado (esperando/en pausa); el ADMIN gobierna
  // la sala desde el menú de pausa (link + fase + equipos/marcador/expulsar).
  const phaseOverlay = new PhaseOverlay();
  // UX-2: cartel de estado de conexión. Solo aparece cuando hay algo que contar
  // (conectando / reconectando / caído); en partida estable no se ve.
  const connBanner = new ConnBanner();
  connBanner.onRetry = () => netplay?.retryNow();
  const onConnState = (state: ConnState, attempt: number): void => {
    // OBS-3 · La historia de la conexión es el contexto que más explica los
    // fallos online: casi todos los "se quedó pillado" vienen detrás de una
    // reconexión. Vocabulario cerrado, sin nada que escriba una persona.
    breadcrumb("net", `conn:${state}${attempt > 0 ? `#${attempt}` : ""}`);
    if (mode !== "online") { connBanner.hide(); return; }
    connBanner.update(state, attempt);
  };
  /**
   * Enlace de invitación: un único parámetro opaco (`?s=…`) que lleva dentro la
   * sala, su región y la contraseña. Ver `lobby/invite.ts` para qué protege
   * exactamente y qué no.
   */
  const inviteLink = (roomId: string): string => inviteUrl(
    `${location.origin}${location.pathname}`,
    { roomId, region: roomRegion, password: roomPassword },
  );
  // El admin lo dice la lista de peers (el creador por defecto; transferible). Si
  // aún no llegó la lista, el creador (slot 0) se asume admin para arrancar la sala.
  const isAdmin = (): boolean => {
    const peers = netplay?.peers ?? [];
    const localSlot = netplay?.client.localSlot ?? 0;
    const me = peers.find((p) => p.slot === localSlot);
    if (me) return me.isAdmin === true;
    return localSlot === 0;
  };

  const startLocal = (): void => {
    breadcrumb("nav", "start-local");
    mode = "local";
    netplay?.dispose();
    netplay = null;
    // El chat es de la PARTIDA en la que se dijo: al entrar a una local se
    // arranca uno nuevo, no se arrastra el de la sala online anterior.
    ui.setChatScope("local");
    phaseOverlay.hide();
    connBanner.hide();
    ui.setOnlineRoom(null);
    ui.setOnlinePeers(null);
    ui.setRoomTeamNames(null); // en local mandan los nombres de los ajustes
    game = createGame(mapById(settings.mapId), rulesFromSettings());
    resultShown = false;
    resetInput();
    acc = 0;
    cam.x = game.courtW / 2;
    cam.y = game.courtH / 2;
  };

  // Mensajes de rechazo del servidor (cierre WS antes del welcome).
  const rejectMessage = (reason: string): string =>
    reason === "bad-password" ? "Contraseña incorrecta."
      : reason === "room-full" ? "La sala está llena o ya en juego."
        : "No se pudo unir a la sala.";

  // autoStart = partida rápida (arranca sola al crearse); las salas CREADAS
  // esperan a que el host pulse EMPEZAR en el overlay de fase.
  const startOnline = (intent: OnlineIntent = { kind: "quick" }, autoStart = false): void => {
    mode = "online";
    roomRegion = intent.region; // edge de esta sala (para el link de invitación)
    roomPassword = intent.kind === "create" || intent.kind === "join" ? intent.password : undefined;
    // Chat nuevo desde el primer momento: mientras el servidor no diga en qué
    // sala estamos, el ámbito es "esta conexión". En cuanto llega el id de sala
    // se refina (ver `scopeRoom`), y volver a la MISMA sala conserva su hilo.
    // Unirse a una sala CONOCIDA reusa su ámbito (volver a la misma sala
    // conserva su conversación); crear o emparejar abre uno nuevo cada vez,
    // porque todavía no hay id y desde luego no es la sala anterior.
    ui.setChatScope(intent.kind === "join" ? `room:${intent.roomId}` : `room:nueva:${++onlineSession}`);
    netplay?.dispose();
    phaseOverlay.hide();
    ui.setOnlineRoom(null);

    const onReject = (reason: string): void => {
      netplay?.dispose();
      netplay = null;
      mode = "local";
      resetInput();
      phaseOverlay.hide();
      connBanner.hide();
      ui.setOnlineRoom(null);
      lobby.openWithError(rejectMessage(reason));
    };
    // Refresca la UI de sala según fase y ROL (admin vs no-admin) SIN abrir/cerrar
    // el menú: solo actualiza controles del admin y el overlay del invitado. Se
    // llama en cada cambio de peers (p. ej. reportes de ping, ~1/s) — por eso NO
    // debe togglear la pausa, o el menú del admin se cerraría solo constantemente.
    // El id de sala llega con la bienvenida: en ese momento el chat pasa a ser
    // el de ESA sala. Si es la misma de antes (reconexión), el hilo continúa.
    const scopeRoom = (): void => {
      if (netplay?.roomId) ui.setChatScope(`room:${netplay.roomId}`);
    };
    const refreshRoomUI = (phase: RoomPhase): void => {
      scopeRoom();
      if (isAdmin()) {
        phaseOverlay.hide();
        ui.setOnlineRoom({ phase, inviteLink: netplay?.roomId ? inviteLink(netplay.roomId) : "" });
      } else {
        // El INVITADO también gobierna lo suyo: entra al MENÚ DE SALA (equipos,
        // mapa, reglas, chat) en vez de a un cartel que solo dice "espera". Ahí
        // puede elegir bando, hablar con la sala y ver quién hay — que es
        // exactamente lo que se hace mientras el anfitrión monta el partido.
        // El cartel de espera se queda solo para la PAUSA en mitad del juego,
        // donde no hay nada que tocar y sí hay algo que explicar.
        ui.setOnlineRoom({ phase, inviteLink: "", guest: true });
        if (phase === "paused") phaseOverlay.update(phase, false);
        else phaseOverlay.hide();
      }
    };
    // Lista de jugadores de la sala: alimenta el panel EQUIPOS (con la FASE, para
    // gatear el arrastre de la ficha propia del no-admin en lobby) y los nombres/
    // colores en cancha, y re-sincroniza el rol (admin pudo cambiar).
    /** Slots que había en la sala la última vez, para contar quién entra y sale. */
    let knownPeers = new Map<number, string>();
    /** true hasta el primer roster: los que YA estaban no "acaban de entrar". */
    let firstRoster = true;

    const updatePeers = (peers: PeerInfo[]): void => {
      const localSlot = netplay?.client.localSlot ?? 0;
      const phase = netplay?.phase ?? "playing";
      // Quién ha llegado y quién se ha ido desde la última lista. Se anuncia en
      // el chat de la sala y suena: sin esto, alguien entraba a tu partida y lo
      // descubrías al chocarte con él.
      const next = new Map<number, string>();
      for (const p of peers) next.set(p.slot, p.name);
      if (!firstRoster) {
        for (const [slot, name] of next) {
          if (knownPeers.has(slot) || slot === localSlot) continue;
          ui.pushChat(`${name} se unió a la sala`, null, true);
          audio.play("join");
        }
        for (const [slot, name] of knownPeers) {
          if (next.has(slot) || slot === localSlot) continue;
          ui.pushChat(`${name} salió de la sala`, null, true);
          audio.play("leave");
        }
      }
      knownPeers = next;
      if (peers.length > 0) firstRoster = false;
      // ONLINE siempre manda la lista del SERVIDOR, aunque venga vacía (todavía
      // no llegó el primer `peers`). Pasar `null` ahí hacía que el panel cayera
      // a la lista LOCAL, que es de otra partida: en una sala 1v0 aparecían
      // nombres de relleno que no estaban en la sala.
      ui.setOnlinePeers({
        peers, localSlot, phase,
        spectators: netplay?.spectators ?? [],
        spectatorCount: netplay?.spectatorCount ?? 0,
      });
      if (netplay) refreshRoomUI(netplay.phase);
    };

    // Última fase vista, para distinguir una TRANSICIÓN real de un mensaje de
    // estado que solo trae otra cosa (p. ej. un cambio de nombres de equipo).
    let lastPhase: RoomPhase | null = null;

    const onState = (phase: RoomPhase): void => {
      breadcrumb("game", `phase:${phase}`);
      // Los nombres de equipo llegan con el estado de sala: el marcador de los
      // dos jugadores muestra lo mismo porque la fuente es el servidor.
      if (netplay) ui.setRoomTeamNames(netplay.teamNames);
      const phaseChanged = phase !== lastPhase;
      lastPhase = phase;
      // Partida rápida: arranca sola al crearse (usa la cancha/reglas por defecto).
      if (phase === "lobby" && autoStart && isAdmin()) { netplay?.control("start"); return; }
      refreshRoomUI(phase);
      // Auto-apertura/cierre del menú del ADMIN SOLO en transiciones de fase
      // REALES: se abre en lobby/pausa, se cierra al (re)entrar a juego. Sin la
      // comprobación, cualquier otro cambio de estado de la sala —renombrar los
      // equipos, sin ir más lejos— le cerraría el menú en las narices al admin
      // justo mientras lo está usando.
      if (phaseChanged) {
        // En lobby se abre el menú de sala para TODOS (el admin para montar el
        // partido, el invitado para colocarse y hablar). En pausa, solo al
        // admin: el invitado ya tiene su cartel, que le dice lo único que puede
        // hacer, que es esperar.
        const wantsPanel = phase === "lobby" || (phase === "paused" && isAdmin());
        if (wantsPanel) { if (!ui.paused) ui.openPause(); }
        // `closePause` se niega mientras la celebración esté bloqueada: el
        // servidor pasa a "over" justo al marcar el gol del final, y ese cambio
        // era el que cerraba la pantalla de victoria en medio segundo.
        else ui.closePause();
      }
      // La fase cambió (lobby→playing…): re-emite el roster para que el panel
      // EQUIPOS habilite/deshabilite el arrastre de la ficha propia (TAREA D).
      if (netplay) updatePeers(netplay.peers);
    };

    // Al entrar en la sala se manda el PERFIL: el nombre ya viaja en la query,
    // pero el icono del disco no, y hasta que la sala lo sepa los demás te ven
    // sin él. Es lo primero que se dice al llegar, antes del primer snapshot.
    const onWelcome = (): void => { netplay?.setProfile({ icon: ui.discIcon }); };
    netplay = createNetplay(
      mapById(settings.mapId), rulesFromSettings(), intent, onReject, onWelcome,
      onState, updatePeers, playerName, onConnState, () => settings.teamNames,
    );
    // Preferencia de extrapolación del jugador (CONFIG · red).
    netplay.client.interpBias = EXTRAPOLATION_BIAS[settings.extrapolation];
    // Chat de la sala: lo que se pinta es SIEMPRE lo que difunde el servidor
    // (incluido el eco del mensaje propio), nunca lo que se tecleó.
    netplay.onChat = (line) => ui.pushRemoteChat(line);
    // NET-4 · Un reinicio anunciado explica el corte que viene después: sin el
    // aviso, un despliegue se vive igual que una caída.
    // El aviso llega cada segundo con la cuenta atrás: el cartel se refresca con
    // todos, pero al chat solo va el PRIMERO. Repetirlo veinte veces enterraría
    // la conversación bajo el mismo mensaje.
    let noticeAnnounced = false;
    netplay.onNotice = (n) => {
      breadcrumb("net", `restart-notice:${n.seconds}`);
      connBanner.setRestartNotice(n.seconds);
      if (noticeAnnounced) return;
      noticeAnnounced = true;
      ui.pushChat(n.message ?? `El servidor se reinicia en ${n.seconds}s. Vuelves solo en cuanto termine.`, null, true);
    };
    // EXPULSIÓN. Antes esto no se manejaba: el socket se cerraba y la persona se
    // quedaba en la cancha congelada bajo un cartel de "desconectado de la sala",
    // sin saber si le habían echado o si se le había caído internet, y sin más
    // salida que recargar. Ahora sale del partido igual que si hubiera pulsado
    // ABANDONAR, pero explicándole quién lo decidió, y aterriza en la lista de
    // salas — que es donde se puede hacer algo: entrar en otra.
    netplay.onKicked = () => {
      breadcrumb("net", "kicked");
      ui.leaveToMenu();
      lobby.openWithError("El anfitrión te ha expulsado de la sala.");
    };
    game = netplay.client.game; // renderizamos el estado predicho
    ui.setRoomTeamNames(netplay.teamNames); // marcador de la SALA, no el local
    updatePeers(netplay.peers); // pinta la lista inicial (loopback ya la trae; WS llega vacía y se rellena)
    renderClock = Math.max(0, netplay.client.latestServerTick);
    resultShown = false;
    resetInput();
    acc = 0;
    cam.x = game.courtW / 2;
    cam.y = game.courtH / 2;
  };

  const restart = (): void => {
    if (mode === "online") {
      // Online: reinicia la MISMA sala con las opciones actuales (solo lo aplica
      // el servidor si eres el host).
      netplay?.control("restart", { mapId: settings.mapId, goalLimit: settings.goalLimit, timeLimit: settings.timeLimit });
    } else startLocal();
  };

  // Lobby online: la lista de salas y el ping vienen de los edges REALES por
  // región (TAREA 3). Con uno o más VITE_REGION_ENDPOINTS (o el VITE_NETPLAY_URL
  // de compat) se agrega la lista de cada edge y se mide su ping real; sin backend
  // configurado se cae al mock offline.
  const edges: Partial<Record<RegionId, string>> = {};
  for (const r of configuredRegions()) { const b = regionHttpBase(r); if (b) edges[r] = b; }
  const roomProvider = hasBackend()
    ? new MultiRegionRoomListProvider(edges)
    : new OfflineRoomListProvider();
  const lobby = new LobbyUI(roomProvider);
  lobby.onExit = () => ui.show();
  // Partida rápida = crea una sala PRIVADA (no listada, sin contraseña) y muestra
  // el link de invitación; sin bot, se espera a un humano por el link.
  lobby.onQuickMatch = () => {
    ui.enterPlaying();
    // Partida rápida: sala privada que arranca sola (autoStart).
    startOnline(
      { kind: "create", name: `Sala de ${playerName || "invitado"}`, password: "", listed: false, autoStart: true },
      true,
    );
  };
  lobby.onJoinRoom = (roomId, password, region) => {
    ui.enterPlaying();
    // La región de la sala decide a qué edge (wss://) conectarse (TAREA 3).
    startOnline({ kind: "join", roomId, password: password ?? undefined, region });
  };
  lobby.onCreateRoom = (opts) => {
    ui.enterPlaying();
    // Sala creada: cancha/reglas por defecto; el admin las ajusta y pulsa EMPEZAR
    // en el menú de pausa (que se auto-abre en el lobby). `capacity` = nº de slots.
    startOnline({ kind: "create", name: opts.name, password: opts.password, listed: opts.listed, region: opts.region, capacity: opts.capacity });
  };
  ui.onPlayOnline = () => { breadcrumb("nav", "open-lobby"); warmUpBackend(); ui.hide(); lobby.open(); };

  // Fondo vivo del menú: una sala real espectada si la hay, y si no una partida
  // de exhibición entre bots simulada aquí mismo (ver `menuBackdrop.ts`).
  const backdrop = new MenuBackdrop({
    findLiveRoom: async () => {
      if (!hasBackend()) return null;
      const snap = await roomProvider.listRooms();
      // Se prefiere una sala EN JUEGO y con gente: un lobby vacío de fondo es
      // una cancha parada, que es justo lo que había antes.
      const playing = snap.rooms
        .filter((r) => r.status === "in-progress" && r.players > 0)
        .sort((a, b) => b.players - a.players);
      const room = playing[0];
      if (!room) return null;
      const base = regionWsUrl(room.region);
      if (!base) return null;
      const sep = base.includes("?") ? "&" : "?";
      return `${base}${sep}spectate=1&room=${encodeURIComponent(room.id)}`;
    },
  });

  ui.onPlay = () => startLocal();
  ui.onRestart = () => restart();
  ui.onQuit = () => {
    netplay?.dispose();
    netplay = null;
    mode = "local";
    resetInput();
    phaseOverlay.hide();
    connBanner.hide();
    ui.setOnlineRoom(null);
    ui.setOnlinePeers(null);
    ui.setRoomTeamNames(null); // vuelve a los nombres locales
  };
  // Controles de sala del host desde el menú de pausa. EMPEZAR/REINICIAR llevan
  // la cancha/reglas actuales (settings); el servidor solo los aplica al host.
  const roomCfg = (): { mapId: string; goalLimit: number; timeLimit: number } =>
    ({ mapId: settings.mapId, goalLimit: settings.goalLimit, timeLimit: settings.timeLimit });
  ui.onRoomStart = () => netplay?.control("start", roomCfg());
  ui.onRoomPause = () => netplay?.control("pause");
  ui.onRoomResume = () => netplay?.control("resume");
  ui.onRoomFinish = () => netplay?.control("finish");
  // Acciones de admin dirigidas (el servidor las valida contra el rol admin).
  ui.onSetTeam = (slot, team) => netplay?.control("setTeam", { targetSlot: slot, team });
  ui.onKick = (slot) => netplay?.control("kick", { targetSlot: slot });
  // SEC-3 · El reporte va al servidor (que lo registra sin difundirlo); el mute
  // NO sale del dispositivo, y por eso lo resuelve la propia UI.
  ui.onReport = (slot, reason) => { netplay?.report(slot, reason); };
  // Nombres de equipo: online los manda al servidor (solo los aplica si eres
  // admin, y los difunde a la sala); en local ya los guardó la propia UI en los
  // ajustes, así que aquí no hay nada más que hacer.
  ui.onSetTeamNames = (names) => { if (mode === "online") netplay?.control("setTeamNames", { teamNames: names }); };
  ui.onTransferAdmin = (slot) => netplay?.control("transferAdmin", { targetSlot: slot });
  ui.onToSpectator = (slot) => netplay?.control("toSpectator", { targetSlot: slot });
  ui.onToPlayer = () => netplay?.control("toPlayer");
  ui.onGrantAdmin = (slot) => netplay?.control("grantAdmin", { targetSlot: slot });
  ui.onRevokeAdmin = (slot) => netplay?.control("revokeAdmin", { targetSlot: slot });
  ui.onApplyRules = (r) => { game.rules.goalLimit = r.goalLimit; game.rules.timeLimit = r.timeLimit; };
  ui.onSelectMap = () => restart();
  // El color del disco se deriva del equipo en cada frame (ui.getLocalTeam()),
  // así que cambiar de equipo se refleja solo; no hay color cacheado que tocar.
  ui.onLocalTeamChange = () => { /* recoloreo en vivo desde el roster de render */ };
  // Online: al servidor (y lo pintará el eco). Local o sin conexión: `false`, y
  // lo pinta la propia UI para que el mensaje no desaparezca en silencio.
  ui.onChatSend = (text) => (mode === "online" && netplay ? netplay.sendChat(text) : false);
  // Al abrir el chat se sueltan las teclas Y el mando táctil: un pulgar apoyado
  // en el joystick no llega a mandar un `pointerup`, así que sin esto el disco
  // seguiría corriendo con la última dirección mientras se escribe.
  // PERFIL: el nombre y el icono se aplican en caliente. Offline se quedan en el
  // dispositivo; online viajan a la sala, que los modera y los difunde.
  ui.onProfileChange = ({ name, icon }) => {
    if (name && name !== playerName) {
      playerName = name;
      savePlayerName(name);
      lobby.setPlayerName(name);
    }
    if (mode === "online") netplay?.setProfile({ name: playerName, icon });
  };

  ui.onTypingChange = (typing) => {
    if (typing) { resetInput(); touch.releaseAll(); }
    // Y se le dice a la sala: los tres puntitos sobre el disco explican por qué
    // este jugador se ha quedado quieto (ver `drawTypingDots`).
    if (mode === "online") netplay?.setTyping(typing);
  };
  ui.getMatch = () => ({
    score: game.score,
    matchTime: game.matchTime,
    timeLimit: game.rules.timeLimit,
    goalLimit: game.rules.goalLimit,
    mapId: game.mapId,
  });
  ui.onSettingsChange = (s) => {
    audio.setEnabled(!s.muted);
    audio.setVolume(s.volume);
    cam.zoomIndex = s.zoom;
    // Todo esto se aplica en caliente: quien está ajustando el color, el tamaño
    // o la calidad necesita VER el efecto para saber si le sirve, no reiniciar.
    teamStyle = teamStyles(s.teamMarkers);
    applyVision(s);
    applyUiScale(s.uiScale);
    keyboard.setBindings(s.keybinds);
    applyQuality(s.quality);
    if (netplay) netplay.client.interpBias = EXTRAPOLATION_BIAS[s.extrapolation];
  };
  // Estado inicial (los ajustes guardados ya están cargados).
  teamStyle = teamStyles(settings.teamMarkers);
  audio.setEnabled(!settings.muted);
  audio.setVolume(settings.volume);

  const resumeAudio = (): void => { audio.resume(); window.removeEventListener("keydown", resumeAudio); };
  window.addEventListener("keydown", resumeAudio);

  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (ui.isTyping()) return;
    if (e.key === "Escape" && ui.getScreen() === "playing" && !game.matchOver) {
      if (ui.paused) ui.closePause();
      else ui.openPause();
    }
  });

  /**
   * ACC-C · La explosión del gol, EN LA CANCHA.
   *
   * Es distinta de `drawGoalAnim`, y las dos hacen falta: aquella es el cartel a
   * pantalla completa que dice qué ha pasado, y esta ocurre **donde ha pasado**,
   * en el punto en que el disco cruzó la línea. Sin ella, el gol se anunciaba
   * pero no se veía entrar.
   *
   * Se lanza desde el evento y no desde el render porque el sitio bueno dura
   * poco: el disco sigue donde cruzó solo hasta que vence `resetTimer`. Leerlo un
   * frame más tarde daría una explosión en el centro del campo.
   *
   * Con REDUCIR DESTELLOS baja de tamaño y de cantidad en vez de desaparecer: un
   * gol tiene que verse. Es el mismo criterio que ya aplica `drawGoalAnim`.
   */
  const explosionDeGol = (): void => {
    if (!settings.effects) return;
    const lado = game.goalSide === 1 ? 1 : 0;
    const w = game.world;
    const bx = w.px[game.ball];
    const by = w.py[game.ball];
    const calma = settings.reduceFlashes;
    // La velocidad del disco INCLINA la explosión, pero no la dirige: el reparto
    // de fondo es radial (ver `spray`). La inclinación es pequeña a propósito,
    // porque en el instante en que se canta el gol el disco ya puede haber
    // rebotado contra el fondo de la red y llevar la velocidad hacia fuera —
    // está comprobado en `test/rings.test.ts`. Con un peso mayor, media
    // explosión saldría disparada de vuelta al campo.
    const drift = calma ? 0 : 0.18;
    goalBits[lado].spray(
      bx, by, calma ? 14 : 30, calma ? 320 : 700,
      w.vx[game.ball] * drift, w.vy[game.ball] * drift,
    );
    const r = w.radius[game.ball];
    rings.spawn(bx, by, r, calma ? r * 5 : r * 11, calma ? 0.4 : 0.55, TEAM_RGB[lado], calma ? 3 : 6);
    if (!calma) rings.spawn(bx, by, r * 0.5, r * 5.5, 0.32, "255,255,255", 3);
  };

  const consumeEvents = (events: GameEvent[]): void => {
    for (const e of events) {
      if (e === "kick") audio.play("kick", 1);
      else if (e === "bounce") audio.play("bounce", game.lastBallSpeed / 400);
      else if (e === "post") audio.play("post");
      else if (e === "goal") {
        // El gol se anuncia con el NOMBRE del equipo que marcó (el mismo que
        // sale en el marcador), no con un rol que no existe en ninguna otra
        // pantalla.
        const names = netplay ? netplay.teamNames : settings.teamNames;
        audio.play("goal");
        ui.pushChat(`¡GOL de ${names[game.goalSide === 1 ? 1 : 0]}!`, null, true);
        explosionDeGol();
      }
      else if (e === "chargeStart") audio.startCharge();
      else if (e === "chargeStop") audio.stopCharge();
    }
  };

  /**
   * Patadas de los RIVALES (online): suenan y chispean igual que las tuyas.
   *
   * El cliente no simula sus teclas, así que su patada no genera evento local:
   * la detecta el netcode al reconciliar (ver `takeRemoteKicks`). Sin esto, la
   * mitad del partido transcurría en silencio — sonaba solo lo que hacías tú.
   */
  const consumeRemoteKicks = (kicks: RemoteKick[]): void => {
    for (const k of kicks) {
      audio.play("kick", k.intensity);
      if (settings.effects) patadaVisible(k.x, k.y, k.nx, k.ny, k.intensity);
    }
  };

  /**
   * ACC-C · Chispas de patada, más la onda de la SUPERPATADA.
   *
   * La patada a máxima carga ya sonaba y pegaba distinto, pero se veía igual que
   * un toque cualquiera: lo único que cambiaba era el número de chispas, y eso no
   * se lee en medio de una jugada. El aro sí — aparece solo arriba del todo de la
   * barra, así que verlo significa una cosa concreta.
   *
   * Una función y no dos llamadas sueltas porque hay DOS caminos hasta aquí (tu
   * patada y la de los rivales, que llega por reconciliación). Con el efecto
   * escrito en cada sitio, el segundo se queda atrás a la primera corrección y la
   * superpatada del rival se ve como un toque normal.
   */
  const patadaVisible = (x: number, y: number, nx: number, ny: number, intensity: number): void => {
    particles.burst(x, y, nx, ny, intensity);
    if (intensity < SUPER_KICK_INTENSITY) return;
    // Con REDUCIR DESTELLOS el aro se queda pequeño y corto, pero no se quita:
    // distingue una jugada de otra, no es decoración.
    const calma = settings.reduceFlashes;
    rings.spawn(x, y, 4, calma ? 26 : 46, calma ? 0.22 : 0.3, "255,255,255", calma ? 2 : 4);
  };

  let vw = window.innerWidth;
  let vh = window.innerHeight;
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  let renderScale = 1;
  const RENDER_SCALES = [1, 0.75, 0.5];
  let scaleIdx = 0;

  /** Fuerza un repintado aunque el canvas esté tapado (ver `covered`). */
  let forceDraw = true;

  const resize = (): void => {
    // MOB-2 · El tamaño sale del viewport VISIBLE, no de `innerHeight`: en móvil
    // ese número no descuenta la barra del navegador y el canvas acababa más
    // alto que el hueco real (con el botón de patada debajo de la barra).
    const view = readViewport();
    vw = view.width;
    vh = view.height;
    dpr = view.dpr;
    renderScale = RENDER_SCALES[scaleIdx];
    forceDraw = true; // el canvas se reasigna en blanco: hay que repintarlo
    const size = canvasSize(view, renderScale);
    canvas.width = size.width;
    canvas.height = size.height;
    canvas.style.width = size.cssWidth + "px";
    canvas.style.height = size.cssHeight + "px";
    ctx.setTransform(size.transform, 0, 0, size.transform, 0, 0);
  };
  resize();
  // Rotar el móvil y que el navegador esconda su barra también cambian el hueco
  // útil, y ninguna de las dos dispara `resize` de forma fiable.
  onViewportChange(resize);

  /**
   * Calidad de render elegida en CONFIG.
   *
   * "auto" deja mandar al escalador adaptativo (el de siempre); las otras dos
   * fijan la resolución y lo apagan — un jugador que elige MÁXIMA no quiere que
   * el juego se la baje solo a mitad de partida, y uno que elige RENDIMIENTO no
   * quiere que se la suba.
   */
  const applyQuality = (q: Quality): void => {
    const next = q === "high" ? 0 : q === "low" ? RENDER_SCALES.length - 1 : scaleIdx;
    lowFpsTime = 0;
    highFpsTime = 0;
    if (next === scaleIdx) return;
    scaleIdx = next;
    resize();
  };

  const cam: Camera = {
    x: game.courtW / 2, y: game.courtH / 2, zoomIndex: settings.zoom,
    zoom: zoomForLevel(settings.zoom, { width: vw, height: vh }, game),
  };
  const particles = new ParticleSystem();
  const trails = new TrailSystem(TEAM_RGB);
  // ACC-C · Ondas expansivas: la explosión de gol y la superpatada. Un solo
  // sistema para las dos, porque son la misma figura (ver `RingSystem`).
  const rings = new RingSystem();
  // Cascotes del gol, un depósito por equipo y APARTE de las chispas de patada.
  //
  // Aparte, porque con uno compartido una explosión se comería las chispas que
  // hubiera en el aire. Y uno por equipo en vez de uno con el color cambiable,
  // porque el color va precalculado por bandas: cambiarlo al vuelo repintaría
  // también los cascotes del gol anterior que siguieran cayendo.
  const goalBits: [ParticleSystem, ParticleSystem] = [
    new ParticleSystem(TEAM_RGB[0], 48, 0.95),
    new ParticleSystem(TEAM_RGB[1], 48, 0.95),
  ];
  // Estela del balón: su propio sistema, con el color del balón y más corta que
  // la de los discos porque el balón va mucho más rápido.
  const ballTrail = new TrailSystem([BALL_RGB, BALL_RGB], BALL_TRAIL_SAMPLES);
  const fx: Efectos = { particles, goalBits, rings, trails, ballTrail };
  // Cámara propia del fondo del menú: sigue al balón con calma (es un plano de
  // ambiente, no una partida que se esté jugando desde aquí).
  const bgCam: Camera = { x: 0, y: 0, zoom: 0.62, zoomIndex: 0 };

  const setZoom = (i: number): void => {
    cam.zoomIndex = Math.max(0, Math.min(ZOOM_LEVELS.length - 1, i));
  };
  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (ui.isTyping()) return;
    if (e.key === "1") setZoom(0);
    else if (e.key === "2") setZoom(1);
    else if (e.key === "3") setZoom(2);
  });

  let acc = 0;
  let lowFpsTime = 0;
  let highFpsTime = 0;
  let last = performance.now();
  let fpsAcc = 0;
  let fpsFrames = 0;
  let fps = 60;
  // Calidad guardada, ya con los contadores del escalador declarados (los toca).
  applyQuality(settings.quality);

  // Al volver a la pestaña, el reloj y el medidor de fps arrancan limpios: el
  // navegador pausa/frena rAF en background, y sin esto el primer frame de vuelta
  // (dt enorme) o la ventana de medición ensuciada bajarían la resolución (pixelado).
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      last = performance.now();
      fpsAcc = 0;
      fpsFrames = 0;
      lowFpsTime = 0;
      highFpsTime = 0;
      // El acumulador de tick se descarta: mientras la pestaña estuvo oculta no
      // hubo frames, y arrastrar ese tiempo dispararía una ráfaga de ticks de
      // recuperación (el disco "teletransportado" al volver).
      acc = 0;
      return;
    }
    // Al IRSE a otra pestaña el navegador congela requestAnimationFrame: dejamos
    // de enviar inputs, pero el servidor sigue simulando. Si el último input que
    // le llegó tenía una tecla pulsada, la repite y el disco se va corriendo
    // solo (el servidor lo neutraliza tras medio segundo, pero ese medio segundo
    // se ve). Soltamos las teclas y empujamos un input neutro ANTES de que se
    // pare el bucle, para que la última orden que quede en el servidor sea
    // "quieto".
    resetInput();
    if (mode === "online" && netplay) netplay.step(input);
  });

  // Interpolación sub-tick (render suave desacoplado del sim de 60 Hz): guardamos
  // las posiciones del tick ANTERIOR y dibujamos en lerp(prev, actual, alpha) con
  // alpha = acc/FIXED_DT. Así el disco se mueve fluido a cualquier refresco
  // (60/120/144 Hz) sin depender del ping ni del ritmo del sim. `prevWorld` detecta
  // recreación de la partida (local↔online, cambio de mapa) para re-sincronizar.
  let prevPx = new Float64Array(game.world.px.length);
  let prevPy = new Float64Array(game.world.py.length);
  prevPx.set(game.world.px);
  prevPy.set(game.world.py);
  let prevWorld = game.world;

  // Visualizador de ping (TAREA C): historial del RTT para el sparkline + reporte
  // periódico del propio ping al servidor (para el ping por usuario del roster).
  const pingHistory: number[] = [];
  let pingSampleAcc = 0;
  let pingReportAcc = 0;
  const PING_HISTORY = 64;

  /**
   * Segundos que la partida lleva sin avanzar y espera antes de rehacer el
   * saludo. La espera crece con cada intento fallido: si el problema es del
   * servidor, reconectar cada dos segundos para siempre solo añade carga al que
   * ya está mal.
   */
  let stallSec = 0;
  let stallAttempts = 0;
  const STALL_REJOIN_SEC = 2;
  const STALL_REJOIN_MAX_SEC = 15;
  const stallWait = (): number =>
    Math.min(STALL_REJOIN_MAX_SEC, STALL_REJOIN_SEC * Math.pow(2, stallAttempts));

  // Estado reutilizado por el bucle de render (cero asignación por frame).
  const MAX_RESTORES = 32;
  const restoreBody = new Int32Array(MAX_RESTORES);
  const restoreX = new Float64Array(MAX_RESTORES);
  const restoreY = new Float64Array(MAX_RESTORES);
  let restoreCount = 0;
  const EMPTY_PEERS: PeerInfo[] = [];
  /** El aviso de servidor viejo se da una vez por sesión, no cada frame. */
  let warnedOutdated = false;
  let rosterPeers: PeerInfo[] = EMPTY_PEERS;
  let rosterLocalTeam: 0 | 1 = 0;
  /** ¿Hay que desconfiar de los slots que la sala no reconoce? (ver `ghostOf`). */
  let rosterGhosts = false;
  const peerOf = (slot: number): PeerInfo | undefined => {
    for (let i = 0; i < rosterPeers.length; i++) if (rosterPeers[i].slot === slot) return rosterPeers[i];
    return undefined;
  };
  const renderRoster: RenderRoster = {
    teamOf: (slot) => {
      const p = peerOf(slot);
      if (p && (p.team === 0 || p.team === 1)) return p.team;
      return slot === renderRoster.localSlot ? rosterLocalTeam : ((slot % 2) as 0 | 1);
    },
    nameOf: (slot) => peerOf(slot)?.name ?? "",
    // Escribiendo: el propio jugador lo sabe la UI; de los demás lo dice el
    // roster que difunde el servidor.
    typingOf: (slot) => (slot === renderRoster.localSlot
      ? ui.isTyping()
      : peerOf(slot)?.typing === true),
    // El propio se pinta con lo elegido en PERFIL aunque el servidor aún no lo
    // haya difundido: tu disco no puede tardar en ser tuyo.
    iconOf: (slot) => iconGlyph(slot === renderRoster.localSlot ? ui.discIcon : peerOf(slot)?.icon),
    // FANTASMA: hay cuerpo en la cancha pero nadie en ese sitio de la sala.
    // Solo puede pasar contra un servidor sin actualizar (uno al día aparca los
    // slots libres y ni siquiera llegan aquí). La lista de la sala sí la manda
    // cualquier versión, así que es ella la que decide quién es una persona.
    ghostOf: (slot) => rosterGhosts && slot !== renderRoster.localSlot && peerOf(slot) === undefined,
    clock: 0,
    localSlot: 0,
    localName: "Tú",
    teamNames: settings.teamNames,
    renderScale: 1,
    showNames: settings.showNames,
    showFps: settings.showFps,
    reduceFlashes: settings.reduceFlashes,
    trails: settings.trails,
  };

  const frame = (now: number): void => {
    const rawElapsed = (now - last) / 1000;
    last = now;
    // Un frame con dt enorme NO es una señal real de fps: pasa cuando el navegador
    // frena/pausa requestAnimationFrame en una pestaña en segundo plano (o ante un
    // stall). Lo marcamos para excluirlo del medidor de fps y no disparar el
    // escalado adaptativo de resolución (que si no bajaría a 0.5x → se pixela).
    const hitch = rawElapsed > 0.25;
    const elapsed = Math.min(rawElapsed, 0.25);

    // MENÚ · fondo vivo. Mientras se está en el menú no hay partida propia que
    // dibujar (está congelada), así que el canvas lo ocupa una partida de
    // verdad: una sala espectada si la hay, y si no la exhibición local. Se
    // atiende aquí y se sale: nada de lo que viene después aplica al menú.
    if (ui.getScreen() === "menu" && !ui.paused) {
      backdrop.start();
      touch.setVisible(false);
      // Con el lobby, el nombre o los legales encima, el canvas no se ve: no se
      // redibuja (el desenfoque de esos paneles se recalcularía 60 veces por
      // segundo sobre algo que nadie mira).
      const hidden = lobby.isOpen || nameScreen.isOpen || legal.isOpen;
      canvas.classList.toggle("tak-menu-backdrop", !hidden);
      if (!hidden) {
        backdrop.update(elapsed);
        const bg = backdrop.game;
        const bw = bg.world;
        const bk = 1 - Math.pow(1 - 0.06, Math.min(4, elapsed * 60));
        bgCam.zoom = effectiveZoom(0.62, { width: vw, height: vh }, { courtW: bg.courtW, courtH: bg.courtH });
        if (bgCam.x === 0 && bgCam.y === 0) { bgCam.x = bg.courtW / 2; bgCam.y = bg.courtH / 2; }
        bgCam.x += (bw.px[bg.ball] - bgCam.x) * bk;
        bgCam.y += (bw.py[bg.ball] - bgCam.y) * bk;
        clampCamera(bgCam, vw, vh, bg.courtW, bg.courtH);
        drawBackdrop(ctx, bg, bgCam, vw, vh, dpr * renderScale);
        ui.setBackdropLive(backdrop.isLive);
      }
      requestAnimationFrame(frame);
      return;
    }
    backdrop.stop();
    canvas.classList.remove("tak-menu-backdrop");

    // Online: si la sala no está "playing" (lobby/pausa/over), el servidor no
    // avanza → congelamos también la predicción local (nada de mover el disco).
    const netFrozen = mode === "online" && netplay ? netplay.phase !== "playing" : false;
    const frozen = ui.getScreen() === "menu" || ui.paused || netFrozen;
    if (!frozen) acc += elapsed;

    // Mantiene válido el buffer de interpolación: si la partida se recreó (cambio
    // local↔online / mapa) o está congelada (menú/pausa), fija prev = actual para
    // que no interpole desde posiciones viejas (con alpha forzado a 1 al dibujar).
    if (game.world !== prevWorld || frozen) {
      if (prevPx.length !== game.world.px.length) {
        prevPx = new Float64Array(game.world.px.length);
        prevPy = new Float64Array(game.world.py.length);
      }
      prevPx.set(game.world.px);
      prevPy.set(game.world.py);
      prevWorld = game.world;
    }

    let steps = 0;
    if (!frozen) while (acc >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
      // Snapshot del tick anterior JUSTO antes de avanzar (para la interpolación
      // sub-tick del render). En frames sin step, prev conserva el tick previo.
      prevPx.set(game.world.px);
      prevPy.set(game.world.py);
      // Online: predice el input local y reconcilia con el servidor autoritativo
      // (loopback o WebSocket real). Local: simula directamente. En ambos casos
      // `game` es el GameState a renderizar (predicho en online).
      if (netplay) netplay.step(input);
      else updateGame(game, input, FIXED_DT);
      consumeEvents(game.events);
      if (netplay) consumeRemoteKicks(netplay.client.takeRemoteKicks());
      particles.update(FIXED_DT);
      // Las partículas son un ajuste (CONFIG · gráficos): con los efectos
      // apagados no se generan, en vez de generarse y no verse.
      if (settings.effects) for (let i = 0; i < game.players.length; i++) {
        const ki = game.kickInfos[i];
        if (ki.active) patadaVisible(ki.x, ki.y, ki.nx, ki.ny, ki.intensity);
      }
      rings.update(FIXED_DT);
      goalBits[0].update(FIXED_DT);
      goalBits[1].update(FIXED_DT);
      acc -= FIXED_DT;
      steps++;
    }
    if (steps === MAX_STEPS_PER_FRAME) acc = 0;

    if (game.matchOver && !resultShown) {
      resultShown = true;
      resetInput();
      ui.showResult(game.winner, game.score);
    } else if (!game.matchOver && resultShown) {
      // Empezó una partida nueva (revancha del anfitrión, o restart local): baja
      // la pantalla de fin y re-arma el flag. Sin esto, el resultado del SIGUIENTE
      // partido no se mostraría nunca — el flag se quedaba pegado en true.
      // `hideResult` puede decir "todavía no": la celebración tiene unos segundos
      // intocables, y una revancha inmediata no debe robárselos. Se reintenta en
      // el frame siguiente, que es justo lo que hace no tocar `resultShown`.
      if (ui.hideResult()) resultShown = false;
    }

    const w = game.world;
    // Fracción del tick pendiente en el acumulador → factor de interpolación del
    // render. Congelado no interpola (dibuja el estado tal cual).
    const alpha = frozen ? 1 : Math.min(1, acc / FIXED_DT);
    // Posición interpolada sub-tick de un cuerpo (lerp del tick previo al actual).
    const rx = (b: number): number => prevPx[b] + (w.px[b] - prevPx[b]) * alpha;
    const ry = (b: number): number => prevPy[b] + (w.py[b] - prevPy[b]) * alpha;

    // Suavizado de cámara/zoom INDEPENDIENTE de fps: el factor por-frame se ajusta
    // por el tiempo transcurrido, así el "feel" es idéntico a 60/120/144 Hz (a 60
    // fps equivale a CAM_LERP). `1 - (1-k)^(dt*60)` = suavizado exponencial estable.
    const frameK = (k: number): number => 1 - Math.pow(1 - k, Math.min(4, elapsed * 60));
    // MOB-2 · El nivel elegido se acota a lo que la pantalla permite: en una
    // pantalla baja, el zoom de escritorio recortaba las porterías por arriba y
    // por abajo. En escritorio el techo queda por encima de cualquier nivel, así
    // que esto no cambia nada allí.
    /* ------------------------- CÁMARA ---------------------------------
     * Encuadra DOS cosas: tu disco y el balón.
     *
     * Seguir solo al disco propio es lo que dejaba al rival (y a la pelota)
     * fuera de cuadro en pantallas estrechas: tú ves tu disco perfectamente y no
     * ves el partido. Con el balón dentro del encuadre siempre, el rival aparece
     * en cuanto disputa la jugada — que es cuando importa verlo.
     *
     * Dos mecanismos, los dos suavizados:
     *  1. El CENTRO se desplaza hacia el balón (pero pesa más tu disco: la
     *     cámara sigue siendo tuya, no del balón, o se pierde la referencia de
     *     hacia dónde te mueves).
     *  2. El ZOOM se abre lo justo para que los dos quepan, y nunca se cierra
     *     más de lo que pide el nivel elegido. Hay suelo: si la pelota está en
     *     la otra punta, alejarse hasta verla convertiría los discos en puntos,
     *     y entonces no se ve nada de nada.
     */
    const camSlot = netplay ? netplay.client.localSlot : 0;
    const camBody = game.players[camSlot] >= 0 ? game.players[camSlot] : game.player;
    // Posiciones INTERPOLADAS (no el tick crudo) para que el encuadre no vibre.
    const meX = rx(camBody);
    const meY = ry(camBody);
    const ballX = rx(game.ball);
    const ballY = ry(game.ball);

    const levelZoom = zoomForLevel(
      cam.zoomIndex,
      { width: vw, height: vh },
      { courtW: game.courtW, courtH: game.courtH },
    );
    // Lo que hay que meter en pantalla, con aire alrededor para que ni el disco
    // ni el balón queden pegados al borde.
    const needW = Math.abs(meX - ballX) + CAM_FRAME_PAD * 2;
    const needH = Math.abs(meY - ballY) + CAM_FRAME_PAD * 2;
    const fitZoom = Math.min(vw / needW, vh / needH);
    const targetZoom = Math.max(levelZoom * CAM_MIN_ZOOM_FACTOR, Math.min(levelZoom, fitZoom));
    cam.zoom += (targetZoom - cam.zoom) * frameK(CAM_ZOOM_LERP);

    const targetX = meX + (ballX - meX) * CAM_BALL_WEIGHT + w.vx[camBody] * CAM_LOOKAHEAD;
    const targetY = meY + (ballY - meY) * CAM_BALL_WEIGHT + w.vy[camBody] * CAM_LOOKAHEAD;
    clampCamera(cam, vw, vh, game.courtW, game.courtH);
    const camK = frameK(CAM_LERP);
    cam.x += (targetX - cam.x) * camK;
    cam.y += (targetY - cam.y) * camK;
    clampCamera(cam, vw, vh, game.courtW, game.courtH);

    // ¿Hay un overlay a pantalla completa tapando el canvas? Entonces el juego
    // no se está viendo: como mucho se intuye difuminado detrás del panel.
    //
    // Esto no es un detalle estético, es EL coste de render de la aplicación.
    // Esos paneles llevan `backdrop-filter: blur()`, y un desenfoque de fondo se
    // recalcula cada vez que cambia lo que hay debajo — es decir, 60 veces por
    // segundo si seguimos redibujando la cancha que nadie ve. Medido en el
    // lobby: 35,8 fps y el escalador tocando fondo (640×360). Sin redibujar
    // debajo, el desenfoque se cachea y vuelve a 60.
    const covered = lobby.isOpen || nameScreen.isOpen || legal.isOpen
      || phaseOverlay.isOpen || ui.paused;

    // MOB-1 · El mando táctil solo existe mientras se ve la cancha: con un menú
    // encima estorba, y si se quedara puesto seguiría capturando los toques
    // destinados a los botones del panel.
    touch.setVisible(ui.getScreen() === "playing" && !covered && touchWanted());

    // Los frames "hitch" (pestaña en background / stall) no cuentan para el fps:
    // así el escalado adaptativo no baja la resolución por un throttle del navegador.
    // Tampoco cuentan los frames tapados, por la misma razón de fondo: medían el
    // coste de un menú, no el del juego, y el escalador acababa castigando la
    // partida por ello — entrabas a jugar y te encontrabas al 75% de resolución.
    if (!hitch && !covered) {
      fpsAcc += elapsed;
      fpsFrames++;
    }
    if (fpsAcc >= 0.5) {
      fps = Math.round(fpsFrames / fpsAcc);
      fpsAcc = 0;
      fpsFrames = 0;
      // Con la calidad fijada a mano (MÁXIMA/RENDIMIENTO) el escalador no toca
      // nada: la resolución la eligió el jugador.
      if (settings.quality !== "auto") { /* resolución fija */ }
      else if (fps < 55) {
        lowFpsTime += 0.5;
        highFpsTime = 0;
        if (lowFpsTime >= 1 && scaleIdx < RENDER_SCALES.length - 1) {
          scaleIdx++;
          resize();
          lowFpsTime = 0;
        }
      } else if (fps >= 58) {
        highFpsTime += 0.5;
        lowFpsTime = 0;
        if (highFpsTime >= 2 && scaleIdx > 0) {
          scaleIdx--;
          resize();
          highFpsTime = 0;
        }
      }
    }

    // Suavizado de render: antes de dibujar, sobrescribimos posiciones SOLO para el
    // render y las restauramos tras `draw` (la simulación sigue intacta). Combina
    // dos capas:
    //  A) INTERPOLACIÓN SUB-TICK (alpha): todo cuerpo que controla/predice el
    //     cliente (tu disco y el balón, y en local todos) se dibuja en lerp(prev,
    //     actual, alpha) → movimiento fluido a cualquier refresco, sin ligarse al
    //     sim de 60 Hz ni al ping.
    //  B) CORRECCIÓN DE RED (online): tu disco y el balón suman el offset de
    //     misprediction que decae (no teletransporta); los RIVALES se interpolan
    //     ~interpDelay ticks en el pasado desde snapshots (delay adaptativo al
    //     jitter) → sin tirones.
    const netLabel = mode === "online" ? (netplay?.status ?? "") : "";
    const localSlot = netplay ? netplay.client.localSlot : 0;
    // Posiciones originales a restaurar tras dibujar. Buffers preasignados en
    // vez de un array de objetos nuevo por frame: son unas pocas entradas, pero
    // se recrean 60 veces por segundo y la basura acaba en pausas del recolector.
    restoreCount = 0;
    const overwrite = (body: number, x: number, y: number): void => {
      restoreBody[restoreCount] = body;
      restoreX[restoreCount] = w.px[body];
      restoreY[restoreCount] = w.py[body];
      restoreCount++;
      w.px[body] = x;
      w.py[body] = y;
    };
    if (netplay) {
      const client = netplay.client;
      // Servidor sin actualizar: no dice qué slots están ocupados, así que sigue
      // moviendo un cuerpo por cada sitio LIBRE de la sala. Esos cuerpos no se
      // dibujan (ver `ghostOf`), pero existen en su simulación y la pelota
      // rebota en ellos. Se avisa UNA vez, diciendo las dos cosas que sirven de
      // algo: por qué pasa y cómo evitarlo sin esperar al despliegue.
      if (client.serverOutdated && !warnedOutdated) {
        warnedOutdated = true;
        ui.pushChat("El servidor de esta región está desactualizado: los sitios libres de la sala siguen ocupando cancha aunque no se vean. Una sala con las plazas justas lo evita.", null, true);
      }
      const latest = client.latestServerTick;
      if (latest >= 0) renderClock += (latest - renderClock) * 0.25;

      // Tu disco: interpolado sub-tick + offset de corrección que decae.
      const localBody = game.players[localSlot] ?? -1;
      if (localBody >= 0) {
        const off = client.localRenderOffset;
        overwrite(localBody, rx(localBody) + off.x, ry(localBody) + off.y);
      }
      // Rivales: en su posición PREDICHA (coasting al presente, interpolada sub-tick)
      // + offset de corrección que decae — el MISMO plano temporal que el balón y
      // que su cuerpo de colisión. Así la pelota rebota justo donde se ve al rival,
      // en vez de atravesar un rival dibujado en el pasado.
      for (let i = 0; i < game.players.length; i++) {
        const b = game.players[i];
        if (i === localSlot || b < 0 || game.active[i] === false) continue;
        const po = client.playerRenderOffset(i);
        overwrite(b, rx(b) + po.x, ry(b) + po.y);
      }
      // Balón: predicho (interpolado sub-tick) + offset de corrección que decae.
      const ballOff = client.ballRenderOffset;
      overwrite(game.ball, rx(game.ball) + ballOff.x, ry(game.ball) + ballOff.y);
    } else {
      // Local: interpolación sub-tick de todos los cuerpos dinámicos.
      for (let i = 0; i < game.players.length; i++) {
        const b = game.players[i];
        if (b >= 0) overwrite(b, rx(b), ry(b));
      }
      overwrite(game.ball, rx(game.ball), ry(game.ball));
    }

    // Roster de render: color por equipo y nombre por slot (de la lista de
    // peers). El objeto y sus funciones se crean UNA vez y se actualizan sus
    // campos; antes se construían enteros en cada frame.
    rosterPeers = netplay?.peers ?? EMPTY_PEERS;
    // Los fantasmas solo existen contra un servidor viejo, y solo si la sala ha
    // llegado a decir quién hay: con la lista vacía (aún no ha llegado) todo
    // slot sería un fantasma y la cancha se quedaría sin nadie.
    rosterGhosts = netplay?.client.serverOutdated === true && rosterPeers.length > 0;
    rosterLocalTeam = ui.getLocalTeam() === 1 ? 1 : 0;
    renderRoster.localSlot = localSlot;
    renderRoster.localName = playerName || "Tú";
    // Nombres de equipo del marcador: los de la SALA en online (el admin los fija
    // y el servidor los difunde), los de los ajustes en local.
    renderRoster.teamNames = netplay ? netplay.teamNames : settings.teamNames;
    renderRoster.renderScale = renderScale;
    renderRoster.showNames = settings.showNames;
    renderRoster.trails = settings.trails;
    renderRoster.showFps = settings.showFps;
    renderRoster.reduceFlashes = settings.reduceFlashes;
    renderRoster.clock = now / 1000;

    const effDpr = dpr * renderScale;
    // Tapado = no se dibuja. `forceDraw` cubre el único caso en que hace falta
    // aunque lo esté: tras un `resize` el canvas se reasigna en blanco, y sin un
    // repintado quedaría un hueco vacío detrás del desenfoque.
    if (!covered || forceDraw) {
      draw(ctx, game, fps, cam, vw, vh, effDpr, fx, netLabel, renderRoster);
      // GP-2 · La cuenta atrás va ENCIMA de la cancha, no en un panel: es el
      // momento de poner las manos en las teclas, y para eso hay que estar
      // mirando el campo, no leyendo un cartel.
      if (netplay && netplay.phase === "countdown") {
        drawCountdown(ctx, vw, vh, netplay.countdown);
      }
      forceDraw = false;
    }
    for (let i = restoreCount - 1; i >= 0; i--) {
      game.world.px[restoreBody[i]] = restoreX[i];
      game.world.py[restoreBody[i]] = restoreY[i];
    }

    // Ping (online): muestrea el RTT para el sparkline y reporta el propio ping al
    // servidor ~1/s. El mini-gráfico va abajo-derecha con el actual y el máximo.
    if (mode === "online" && netplay) {
      const cur = netplay.client.pingMs;
      pingSampleAcc += elapsed;
      pingReportAcc += elapsed;
      if (pingSampleAcc >= 0.2) {
        pingSampleAcc = 0;
        pingHistory.push(cur);
        if (pingHistory.length > PING_HISTORY) pingHistory.shift();
      }
      if (pingReportAcc >= 1) { pingReportAcc = 0; netplay.reportPing(cur); }
      // Conectados pero sin que el servidor confirme nuestros inputs: la partida
      // se ve fluida por la predicción local, pero el jugador no se mueve de
      // verdad. Se avisa en vez de dejarlo adivinando si se ha desconectado.
      // …pero SOLO si la sala está jugando. Si está en lobby, en pausa o
      // terminada, el servidor no consume inputs porque no debe: no hay nada que
      // sincronizar. Anunciarlo como fallo de red era mentir sobre la causa, y
      // peor aún, disparaba una reconexión cada pocos segundos para siempre —
      // reconectar no puede arrancar una sala que nadie ha empezado.
      const stalled = netplay.client.inputStalled && netplay.phase === "playing";
      connBanner.setInputStall(stalled);
      // Y no se le deja ahí indefinidamente: si a los 2 segundos la partida
      // sigue sin avanzar, se rehace el saludo con la sala. Un cartel eterno no
      // es información, es un juego roto con subtítulos.
      if (stalled) {
        stallSec += elapsed;
        if (stallSec >= stallWait()) {
          stallSec = 0;
          stallAttempts++;
          netplay.forceRejoin();
        }
      } else {
        stallSec = 0;
        stallAttempts = 0; // volvió a fluir: se re-arma el primer intento rápido
      }
      if (settings.showPing) {
        drawPingGraph(ctx, vw, vh, effDpr, pingHistory, cur, netplay.client.pingMaxMs,
          netplay.client.hasPing, netplay.client.jitterMs);
      }
    }
    requestAnimationFrame(frame);
  };
  // Arranque: la PRIMERA pantalla (entrada propia o por invitación) es elegir el
  // nombre visible en partida. Con `?join=<sala>` se entra directo a esa sala
  // (deep-link de invitación); si no, al menú.
  const params = new URLSearchParams(location.search);
  // La invitación llega en un token opaco (`?s=`); los enlaces antiguos en claro
  // (`?join=&region=&pw=`) siguen entendiéndose. Ver `lobby/invite.ts`.
  const invite = readInvite(params);
  const joinRoomId = invite?.roomId ?? null;
  const joinPw = invite?.password ?? null;
  const joinRegion = invite?.region; // edge de la sala (multi-región)

  const proceedAfterName = (): void => {
    if (joinRoomId) {
      ui.enterPlaying();
      startOnline({ kind: "join", roomId: joinRoomId, password: joinPw ?? undefined, region: joinRegion });
    } else {
      ui.show();
    }
  };

  const nameScreen = new NameScreen();
  const legal = new LegalScreen();
  nameScreen.onOpenLegal = (doc) => legal.open(doc);
  const stored = loadPlayerName();
  playerName = stored ?? "";
  if (stored) { lobby.setPlayerName(stored); ui.setPlayerName(stored); }
  nameScreen.open(
    stored ?? "",
    joinRoomId ? "TE INVITARON A JUGAR" : "ELIGE TU NOMBRE",
    (name) => {
      playerName = name;
      savePlayerName(name);
      // A partir de aquí el jugador va a jugar seguro: se adelanta el coste de
      // DNS/TCP/TLS (y de despertar el servidor) para que al pulsar JUGAR la
      // conexión ya esté caliente.
      warmUpBackend();
      lobby.setPlayerName(name);
      ui.setPlayerName(name);
      proceedAfterName();
      // Lo último: el portal ya está cerrado y el menú pintado, así que la
      // pantalla que traiga la vuelta del proveedor queda encima y no se borra.
      aplicarVuelta();
    },
  );
  requestAnimationFrame(frame);

  // El juego vive lo que vive la pestaña: no hay ruta que desmonte el teclado.
  // Se referencia para dejar claro que la retención es deliberada.
  void keyboard;
}

main();
